import { formatProductionBusinessDate } from '../productionBusinessDates';
// JobCardModal.jsx
import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import { appendProductionInstanceScope } from "../productionRequestScope";

import ChatBox from "../../sales/orders/orders-single/order-components/orderProgressBar/ChatBox";
import {
  assignCoreNetworkWorkRequest,
  cancelCoreNetworkOffer,
  completeCoreNetworkAssignment,
  createCoreNetworkProductionWorkRequest,
  createCoreNetworkRating,
  generateCoreNetworkMatches,
  getCoreNetworkWorkRequest,
  listCoreNetworkEvents,
  listCoreNetworkMatches,
  listCoreNetworkOffers,
  listCoreNetworkRatings,
  listCoreNetworkWorkRequests,
  sendCoreNetworkOffers,
  shortlistCoreNetworkMatches,
  updateCoreNetworkProviderStatus,
} from "../../../../services/coreNetwork";
import CoreNetworkMatchPanel from "../../core-network/components/CoreNetworkMatchPanel";
import CoreNetworkOfferPanel from "../../core-network/components/CoreNetworkOfferPanel";
import CoreNetworkAssignmentPanel from "../../core-network/components/CoreNetworkAssignmentPanel";

const productionAuthHeaders = (extra = {}) => {
  const token = localStorage.getItem("accessToken");
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

const fetch = (input, init = {}) =>
  window.fetch(appendProductionInstanceScope(input, {
    pathname: window.location.pathname,
    apiBase: process.env.REACT_APP_NODEJS_SERVER_URL,
  }), {
    ...init,
    headers: productionAuthHeaders(init.headers || {}),
  });


/**
 * JobCardModal - Reusable job card modal
 */


// SweetAlert wrapper
const SA = {
  info: (text, title = "Info") => {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "info", title, text })
      : Promise.resolve(alert(`${title}\n${text}`));
  },
  error: (text, title = "Error") => {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "error", title, text })
      : Promise.resolve(alert(`${title}\n${text}`));
  },
  success: (text, title = "Success") => {
    const s = window.Swal || window.swal;
    return s?.fire
      ? s.fire({ icon: "success", title, text })
      : Promise.resolve(alert(`${title}\n${text}`));
  },
};

// Date formatter
const DATE_FMT = (d) =>
  d ? new Date(d).toLocaleDateString("en-CA") : ""; // yyyy-mm-dd

// Update array at index helper
const updateAt = (setter, idx, newVal) => {
  setter((prev) => {
    const next = [...prev];
    next[idx] = newVal;
    return next;
  });
};

// Constants
const STATUSES = [
  { key: "Planned", label: "Upcoming Orders" },
  { key: "Scheduled", label: "Scheduled" },
  { key: "InProduction", label: "In Production" },
  { key: "QC", label: "QC" },
  { key: "OnHold", label: "Paused / Hold" },
  { key: "Completed", label: "Completed" },
];

const SHOW_QC_LANE = false; // Set to true to enable QC lane

// Modal styles
const modal = {
  overlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 9999,
  },


 card: {
    background: "#fff",
    borderRadius: "8px",
    width: "90vw", // 90% of screen width
    maxWidth: "1200px", // Max 1200px (good for most screens)
    maxHeight: "90vh", // 90% of screen height
    overflow: "auto",
    boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
  },
  header: {
    padding: "16px 20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
};

// UI button styles
const ui = {
  btnPrimary: {
    padding: "8px 16px",
    background: "#3b82f6",
    color: "#fff",
    border: "none",
    borderRadius: "6px",
    fontWeight: "600",
    cursor: "pointer",
  },
  btnGhost: {
    padding: "8px 16px",
    background: "transparent",
    color: "#6b7280",
    border: "1px solid #d1d5db",
    borderRadius: "6px",
    fontWeight: "600",
    cursor: "pointer",
  },
  subtle: {
    fontSize: "12px",
    color: "#6b7280",
    marginBottom: "4px",
  },
};



export function JobCardModal({
  open,
  onClose,
  card,
  modalLoading,
  detailedCard,
  orderMeta,
  orderCosting,
  orderCharges,
  onPatchCard,
  onCancelCard,
  onReinstateCard,
  siblingsAll = [],
  siblingsSameLane = [],
  onOpenSplit,
  onOpenJoin,
}) {
   const { instanceUniqueId } = useParams();
   const [tab, setTab] = useState("overview");
    const [bom, setBOM] = useState(card?.bom || []);
    const [chat, setChat] = useState([]);
    const [chatLoading, setChatLoading] = useState(false);
    const [chatText, setChatText] = useState("");
  
    // JobCardModal
    const [auditEvents, setAuditEvents] = useState([]);
    const [bomHeaderId, setBomHeaderId] = useState(null);
    const [editingBomIdx, setEditingBomIdx] = useState(null);
    const [bomBackup, setBomBackup] = useState(null);
    const [processes, setProcesses] = useState([]);
  
    // ======= Edit State =======
    const [editingProcessIdx, setEditingProcessIdx] = useState(null);
    const [processBackup, setProcessBackup] = useState(null);
  
    const [auditFilters, setAuditFilters] = useState({
      department: "",
      types: "",
      scope: "full",
      withMeta: false,
    });
    const [networkLoading, setNetworkLoading] = useState(false);
    const [networkBusy, setNetworkBusy] = useState("");
    const [networkError, setNetworkError] = useState("");
    const [networkRequest, setNetworkRequest] = useState(null);
    const [networkMatches, setNetworkMatches] = useState([]);
    const [networkOffers, setNetworkOffers] = useState([]);
    const [networkEvents, setNetworkEvents] = useState([]);
    const [networkRatings, setNetworkRatings] = useState([]);
    const [selectedMatchIds, setSelectedMatchIds] = useState([]);
    const [providerStatus, setProviderStatus] = useState("offered");
    const [providerStatusNote, setProviderStatusNote] = useState("");
    const [ratingDraft, setRatingDraft] = useState({
      overallScore: "",
      qualityScore: "",
      communicationScore: "",
      onTimeScore: "",
      defectScore: "",
      comments: "",
    });
  
    const enhancedCard = {
      background: "white",
      borderRadius: "8px",
      border: "1px solid #e5e7eb",
      overflow: "hidden",
      boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
      height: "fit-content",
      transition: "box-shadow 0.2s ease",
    };
  
    const cardHeader = {
      padding: "14px 18px",
      background: "#fafafa",
      borderBottom: "1px solid #e5e7eb",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
    };
  
    const cardTitle = {
      margin: 0,
      fontSize: "13px",
      fontWeight: 600,
      color: "#374151",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };
  
    const cardBadge = {
      padding: "3px 8px",
      background: "#f3f4f6",
      color: "#6b7280",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: 600,
      border: "1px solid #e5e7eb",
    };
  
    const cardBody = {
      padding: "18px",
    };
  
    const dataRowStyle = {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      padding: "10px 0",
      borderBottom: "1px solid #f9fafb",
    };
  
    const labelStyle = {
      fontSize: "12px",
      color: "#6b7280",
      fontWeight: 500,
      flex: "0 0 45%",
    };
  
    const valueStyle = {
      fontSize: "13px",
      color: "#111827",
      fontWeight: 500,
      textAlign: "right",
      flex: "1",
    };
  
    const sectionDivider = {
      height: "1px",
      background: "#e5e7eb",
      margin: "16px 0",
    };
  
    const chipStyle = {
      display: "inline-block",
      padding: "4px 10px",
      background: "#f3f4f6",
      color: "#374151",
      borderRadius: "4px",
      fontSize: "11px",
      fontWeight: 600,
      border: "1px solid #e5e7eb",
    };
  
    const decorationTableStyle = {
      width: "100%",
      borderCollapse: "collapse",
      fontSize: "13px",
    };
  
    const decorationTableHeaderStyle = {
      background: "#fafafa",
      borderBottom: "2px solid #e5e7eb",
      textAlign: "left",
      padding: "10px 12px",
      fontWeight: 600,
      color: "#374151",
      fontSize: "11px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    };
  
    const decorationTableCellStyle = {
      padding: "10px 12px",
      borderBottom: "1px solid #f9fafb",
      color: "#111827",
      fontSize: "13px",
    };
  
    // FIXED: Simple helper function without hooks
    const renderDataRow = (label, value, options = {}) => {
      const { highlight = false, multiline = false, mono = false } = options;
  
      return (
        <div style={dataRowStyle} key={label}>
          <span style={labelStyle}>{label}</span>
          <span
            style={{
              ...valueStyle,
              fontWeight: highlight ? 600 : 500,
              color: highlight ? "#111827" : "#374151",
              fontFamily: mono ? "monospace" : "inherit",
              whiteSpace: multiline ? "normal" : "nowrap",
              lineHeight: multiline ? "1.5" : "normal",
            }}
          >
            {value}
          </span>
        </div>
      );
    };
  
    // CSS
    const hoverStyles = `
    .data-row:hover {
      background: #fafafa;
    }
    
    .table-row:hover {
      background: #fafafa !important;
    }
    
    .hover-card:hover {
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
    }
  `;

    const productionJobId = Number(card?.pwoid || card?.pwo_id || 0) || null;
    const productionStepId = Number(card?.step_id || 0) || null;
    const activeAssignments = Array.isArray(networkRequest?.assignments) ? networkRequest.assignments : [];
    const activeAssignment = activeAssignments[0] || null;
    const ratedMemberId = activeAssignment?.memberId || activeAssignment?.member?.id || null;
    const currentDetailItem = detailedCard?.Items?.[0] || null;
    const currentDecoration = currentDetailItem?.decorations?.[0] || null;
    const selectedPrimaryMatch = useMemo(
      () => networkMatches.find((match) => selectedMatchIds.includes(match.id)) || null,
      [networkMatches, selectedMatchIds]
    );
    const providerStatusOptions = [
      "offered",
      "accepted",
      "art_review",
      "proof_required",
      "proof_sent",
      "proof_approved",
      "queued",
      "in_production",
      "quality_check",
      "packed",
      "shipped",
      "delivered",
      "completed",
      "revision_requested",
      "cancelled",
    ];
    const selectedMatchesCount = selectedMatchIds.length;

    const loadDecoratorNetwork = async () => {
      if (!instanceUniqueId || !productionJobId) return;
      try {
        setNetworkLoading(true);
        setNetworkError("");
        const requestsResponse = await listCoreNetworkWorkRequests({
          instanceUniqueId,
          productionJobId,
          requestType: "decoration-production",
        });
        const requests = Array.isArray(requestsResponse?.workRequests)
          ? requestsResponse.workRequests
          : Array.isArray(requestsResponse)
            ? requestsResponse
            : [];
        const latestRequest = requests[0] || null;
        setNetworkRequest(latestRequest);
        setSelectedMatchIds([]);
        setNetworkMatches([]);
        setNetworkOffers([]);
        setNetworkEvents([]);
        setNetworkRatings([]);

        if (!latestRequest?.id) {
          return;
        }

        const [requestResponse, matchesResponse, offersResponse, eventsResponse, ratingsResponse] = await Promise.all([
          getCoreNetworkWorkRequest(latestRequest.id, instanceUniqueId),
          listCoreNetworkMatches(latestRequest.id, { instanceUniqueId }),
          listCoreNetworkOffers(latestRequest.id, { instanceUniqueId }),
          listCoreNetworkEvents(latestRequest.id, { instanceUniqueId }),
          listCoreNetworkRatings(latestRequest.id, { instanceUniqueId }),
        ]);

        const hydratedRequest = requestResponse?.workRequest || latestRequest;
        setNetworkRequest(hydratedRequest);
        setNetworkMatches(Array.isArray(matchesResponse?.matches) ? matchesResponse.matches : []);
        setNetworkOffers(Array.isArray(offersResponse?.offers) ? offersResponse.offers : []);
        setNetworkEvents(Array.isArray(eventsResponse?.events) ? eventsResponse.events : []);
        setNetworkRatings(Array.isArray(ratingsResponse?.ratings) ? ratingsResponse.ratings : []);
        setProviderStatus(hydratedRequest?.rawStatus || hydratedRequest?.status || "offered");
      } catch (error) {
        setNetworkError(error?.response?.data?.message || "Failed to load Decorator Network data.");
      } finally {
        setNetworkLoading(false);
      }
    };

    useEffect(() => {
      if (!open || tab !== "decoratorNetwork") return;
      loadDecoratorNetwork();
    }, [open, tab, instanceUniqueId, productionJobId]);

    const toggleSelectedMatch = (matchId) => {
      setSelectedMatchIds((prev) =>
        prev.includes(matchId) ? prev.filter((id) => id !== matchId) : [...prev, matchId]
      );
    };

    const runNetworkAction = async (actionKey, callback) => {
      try {
        setNetworkBusy(actionKey);
        setNetworkError("");
        await callback();
        await loadDecoratorNetwork();
      } catch (error) {
        setNetworkError(error?.response?.data?.message || error?.message || "Decorator Network action failed.");
      } finally {
        setNetworkBusy("");
      }
    };

    const buildProductionWorkRequestPayload = () => {
      const artworkFiles = Array.isArray(currentDecoration?.artworks)
        ? currentDecoration.artworks
            .map((artwork) => ({
              fileId: artwork?.id ? `artwork-${artwork.id}` : artwork?.Image,
              fileName: artwork?.LogoName || artwork?.ImprintType || "Artwork File",
              filePath: artwork?.Image || "",
              artworkId: artwork?.id || null,
            }))
            .filter((file) => file.filePath)
        : [];
      const proofFiles = Array.isArray(orderMeta?.proofsNormalized)
        ? orderMeta.proofsNormalized
            .map((proof) => ({
              fileId: proof?.id ? `proof-${proof.id}` : proof?.filePath,
              fileName: proof?.poNumber ? `${proof.poNumber} Proof` : "Proof File",
              filePath: proof?.filePath || proof?.previewUrl || "",
            }))
            .filter((file) => file.filePath)
        : [];
      const productionFiles = Array.isArray(orderMeta?.productionFiles)
        ? orderMeta.productionFiles
            .map((file, index) => ({
              fileId: file?.sourceFileId || file?.fileId || file?.id || `production-file-${index}`,
              fileName:
                file?.fileName ||
                file?.sourceFileName ||
                (file?.filePath || file?.sourceFilePath
                  ? String(file.filePath || file.sourceFilePath).split("/").pop()
                  : "Production File"),
              filePath: file?.filePath || file?.sourceFilePath || file?.downloadUrl || file?.url || "",
              productionOnly: true,
            }))
            .filter((file) => file.filePath)
        : [];

      return {
        instanceUniqueId,
        stepId: productionStepId,
        title: `${card?.projectName || card?.title || currentDetailItem?.Name || "Production Job"} - ${card?.processName || currentDecoration?.Location || "Decorator Network"}`,
        description: `Decorator Network request for ${card?.productName || currentDetailItem?.Name || "production job"} (${card?.processLocation || currentDecoration?.Location || "location pending"})`,
        instructions: card?.meta?.blockedReason || "",
        deadlineAt: card?.dueDate || detailedCard?.inHandsDate || null,
        priority: Number(card?.rank || 0) <= 3 ? "high" : "normal",
        budgetAmount: card?.retailCost || "",
        filesJson: [...artworkFiles, ...proofFiles, ...productionFiles],
        shipToJson: {
          name: orderMeta?.customerName || detailedCard?.CustomerName || "",
          city: "",
          province: "",
          country: "",
        },
        garmentType: currentDetailItem?.Name || card?.productName || "",
        budgetTier: card?.retailCost ? "standard" : "",
      };
    };
  
    useEffect(() => {
      if (!open) return;
  
      const ek = card.external_key || card.externalkey;
  
      if (!ek && auditFilters.scope !== "card") {
        console.log("No external key found");
        return;
      }
  
      // Build API URL based on filters
      let apiUrl = `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/audit?`;
      const params = new URLSearchParams();
  
      // Determine which identifier to use
      if (auditFilters.scope === "card" && card.step_id) {
        params.append("stepId", card.step_id);
        params.append("includeBatch", "true");
      } else if (ek) {
        params.append("external_key", ek);
        params.append("scope", auditFilters.scope);
      }
  
      if (auditFilters.department) {
        params.append("department", auditFilters.department);
      }
  
      if (auditFilters.types) {
        params.append("types", auditFilters.types);
      }
  
      if (auditFilters.withMeta) {
        params.append("withMeta", "true");
      }
  
      params.append("limit", "200");
  
      fetch(`${apiUrl}${params.toString()}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((js) => {
          console.log("Audit response:", js);
          // setAuditEvents(js.events ?? []);
  
          // })
  
          const mappedEvents = (js.events ?? []).map((ev) => ({
            id: ev.timestamp || ev.id,
            at: ev.timestamp, // KEY CHANGE: map timestamp to at
            type: ev.type,
            from: ev.from,
            to: ev.to,
            note: ev.note,
            department: ev.department,
            machineName: ev.machineName,
            machineCode: ev.machineCode,
            step_id: ev.step_id,
            seq: ev.seq,
            lane: ev.lane,
            batch_group_id: ev.batch_group_id,
            source: ev.source,
          }));
  
          setAuditEvents(mappedEvents);
        })
        .catch((err) => {
          console.error("Audit fetch failed:", err);
          setAuditEvents([]);
        });
    }, [open, card.external_key, card.externalkey, card.step_id, auditFilters]);
  
    // ======= Fetch Processes =======
    useEffect(() => {
      if (!open || !card) {
        setProcesses([]);
        return;
      }
  
      async function fetchProcesses() {
        try {
          const params = new URLSearchParams();
          if (card.orderDbId) params.append("orderId", card.orderDbId);
          if (card.itemId) params.append("itemId", card.itemId);
          if (card.pwoid) params.append("pwoId", card.pwoid);
  
          // if (card.externalkey) params.append("external_key", card.externalkey);
          // if (card.external_key) params.append("external_key", card.external_key);
  
          // THIS ONE LINE (checks both properties)
          if (card.externalkey || card.external_key) {
            params.append("external_key", card.externalkey || card.external_key);
          }
  
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes?${params}`
          );
          const data = await response.json();
  
          if (data.success && data.data.length > 0) {
            const items = data.data.map((item) => ({
              id: item.id,
              step_no: item.step_no || 1,
              name: item.name || "",
              person: item.person || "",
              type: item.type || "manual",
              machine_id: item.machine_id || null,
              machine_name: item.machine?.name || "",
              est_minutes: item.est_minutes || null,
              due_date: item.due_date || null,
              cost: item.cost || null,
              notes: item.notes || "",
              department_id: item.department_id || null,
              department_name: item.Department?.name || "",
            }));
            setProcesses(items);
          } else {
            setProcesses([]);
          }
        } catch (err) {
          console.error("Failed to fetch processes:", err);
          setProcesses([]);
        }
      }
  
      fetchProcesses();
    }, [open, card]);
  
    useEffect(() => {
      if (!open) return;
  
      const ek = card.external_key;
  
      console.log("card", card);
      console.log("card.external_key", card.external_key);
  
      console.log("card?.externalkey", card?.externalkey);
      console.log(
        "detailedCard?.meta?.external_key",
        detailedCard?.meta?.external_key
      );
      console.log("Final ek:", ek);
  
      if (!ek) {
        console.log("No external key found, skipping audit fetch");
        return;
      }
  
      fetch(
        `${
          process.env.REACT_APP_NODEJS_SERVER_URL
        }/production/audit?external_key=${encodeURIComponent(ek)}`
      )
        .then((r) => (r.ok ? r.json() : Promise.reject(r)))
        .then((js) => {
          console.log("Audit response:", js);
          setAuditEvents(js.events ?? []);
        })
        .catch((err) => {
          console.error("Audit fetch failed:", err);
          setAuditEvents([]);
        });
    }, [
      open,
      card?.externalkey,
      card?.external_key,
      detailedCard?.meta?.external_key,
    ]);
  
    useEffect(() => {
      if (!open || !card) return;
      setTab("overview");
      setProcesses(card.processes);
      setBOM(card.bom);
  
      // Chat messages are handled via /chat/messages API endpoint
      // No need to fetch step-specific messages here
    }, [open, card]);
  
    useEffect(() => {
      if (!open || !card) {
        setBOM([]);
        setBomHeaderId(null);
        return;
      }
  
      async function fetchBOM() {
        try {
          const params = new URLSearchParams();
  
          if (card.orderDbId) params.append("orderId", card.orderDbId);
          if (card.itemId) params.append("itemId", card.itemId);
          if (card.pwo_id) params.append("pwoId", card.pwo_id);
          if (card.external_key) params.append("external_key", card.external_key);
  
          console.log("🔍 BOM fetch params:", params.toString());
          console.log("🔍 Card data:", {
            orderDbId: card.orderDbId,
            itemId: card.itemId,
            pwo_id: card.pwo_id,
            external_key: card.external_key,
          });
  
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom?${params}`
          );
          const data = await response.json();
  
          console.log("✅ BOM API Response:", data);
  
          if (data.success && data.data.length > 0) {
            const bomHeader = data.data[0];
            setBomHeaderId(bomHeader.id);
  
            const items = (bomHeader.items || []).map((item) => ({
              id: item.id,
              sku: item.sku || "",
              description: item.description || "",
  
              qtyper:
                item.qty_per !== null && item.qty_per !== undefined
                  ? parseFloat(item.qty_per)
                  : "",
              unit: item.unit || "",
  
              unitprice:
                item.unit_price !== null && item.unit_price !== undefined
                  ? parseFloat(item.unit_price)
                  : "",
              cost: parseFloat(item.cost) || null,
              source: item.source || "stock",
              location: item.location || "",
  
              scrappct:
                item.scrap_pct !== null && item.scrap_pct !== undefined
                  ? parseFloat(item.scrap_pct)
                  : 0,
            }));
  
            setBOM(items);
          } else {
            setBomHeaderId(null);
            setBOM([]);
          }
        } catch (err) {
          console.error("❌ Failed to fetch BOM:", err);
          setBOM([]);
        }
      }
  
      fetchBOM();
    }, [open, card]);
  
    if (!open || !card) return null;
    const o = card.raw?.order || {};
    const it = card.raw?.item || {};
    const d = card.raw?.deco || {};
    const ctx = {
      customer: card.customer || o.customerName,
      qty: it.quantity ?? card.qtyPlanned ?? 0,
      inHandsDate: o.inHandsDate || card.dueDate,
    };
  
    const totalAcross =
      siblingsAll.reduce((t, c) => t + (Number(c.qtyPlanned || 0) || 0), 0) ||
      card.rootQty ||
      ctx.qty ||
      0;
    const isBatch = !!card.rootId || siblingsAll.length > 1;
    const qtyDisplay = isBatch
      ? `${card.qtyPlanned ?? 0} / ${totalAcross}`
      : card.qtyPlanned ?? ctx.qty ?? 0;
  
    // ======= Simple Add Process =======
    const addProcess = () =>
      setProcesses((p) => [
        ...p,
        {
          id: `proc-${Date.now()}`,
          step_no: (p.length || 0) + 1,
          name: "",
          person: "",
          type: "manual",
          machine_id: null,
          machine_name: "",
          est_minutes: null,
          due_date: null,
          cost: null,
          notes: "",
          // department_id: departmentMap[department]?.id || null,
        },
      ]);
  
    const addBOM = () =>
      setBOM((b) => [
        ...b,
        {
          id: `bom-${Date.now()}`,
          sku: "",
          description: "",
          qtyper: "",
          unit: "",
          unitprice: "",
          supplier: "",
          cost: "",
        },
      ]);
  
    // 1. UPDATE the removeBOM function to call the DELETE API
    const removeBOM = async (idx) => {
      const item = bom[idx];
  
      if (String(item.id).startsWith("bom-")) {
        setBOM((b) => b.filter((_, i) => i !== idx));
        return;
      }
  
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          }
        );
  
        const data = await response.json();
  
        if (data.success) {
          // Remove from UI after successful deletion
          setBOM((b) => b.filter((_, i) => i !== idx));
          SA.success("BOM item deleted", "Success");
        } else {
          throw new Error(data.message || "Failed to delete");
        }
      } catch (err) {
        SA.error("Failed to delete BOM item", err.message);
      }
    };
  
    const saveMeta = async () => {
      try {
        // Save processes
        if (card.isStep) {
          await fetch(
            `${
              process.env.REACT_APP_NODEJS_SERVER_URL
            }/production/steps/${encodeURIComponent(card.id)}/processes`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify(processes),
            }
          );
        }
  
        //  FIXED: Save BOM data
        if (bom && bom.length > 0) {
          let currentBomHeaderId = bomHeaderId; // Track the current header ID
  
          if (!currentBomHeaderId) {
            console.log("Creating new BOM header with all items...");
  
            const headerResponse = await fetch(
              `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  order_id: card.orderDbId,
                  order_item_id: card.itemId,
                  pwo_id: card.pwoid,
                  // external_key: card.externalkey,
                  external_key: card.external_key || card.externalkey,
                  notes: "",
                  version: 1,
                  // Include ALL items when creating header
                  items: bom.map((item) => ({
                    sku: item.sku,
                    description: item.description,
                    qty_per: item.qtyper,
                    unit: item.unit,
                    unit_price: item.unitprice,
                    cost: item.cost,
                    source: item.source,
                    location: item.location,
                    scrap_pct: item.scrappct,
                  })),
                }),
              }
            );
  
            const headerData = await headerResponse.json();
            console.log("BOM header response:", headerData);
  
            if (headerData.success) {
              currentBomHeaderId = headerData.id;
              setBomHeaderId(headerData.id);
              console.log("BOM header created with ID:", headerData.id);
  
              // Success! All items were created with the header
              SA.success("BOM saved successfully!", "Saved");
            } else {
              throw new Error(headerData.message || "Failed to create BOM");
            }
          } else {
            // Header already exists - update individual items
            console.log(
              "Updating existing BOM items for header:",
              currentBomHeaderId
            );
  
            for (const item of bom) {
              if (String(item.id).startsWith("bom-")) {
                // New item - POST it to existing header
                console.log(
                  "Adding new item to existing header:",
                  currentBomHeaderId
                );
  
                const addResponse = await fetch(
                  `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/${currentBomHeaderId}/items`,
                  {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      sku: item.sku,
                      description: item.description,
                      qty_per: item.qtyper,
                      unit: item.unit,
                      unit_price: item.unitprice,
                      cost: item.cost,
                      source: item.source,
                      location: item.location,
                      scrap_pct: item.scrappct,
                    }),
                  }
                );
  
                const addData = await addResponse.json();
                if (!addData.success) {
                  throw new Error(addData.message || "Failed to add item");
                }
              } else {
                // Existing item - PATCH it
                console.log("Updating existing item:", item.id);
  
                const updateResponse = await fetch(
                  `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
                  {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                      sku: item.sku,
                      description: item.description,
                      qty_per: item.qtyper,
                      unit: item.unit,
                      unit_price: item.unitprice,
                      cost: item.cost,
                      source: item.source,
                      location: item.location,
                      scrap_pct: item.scrappct,
                    }),
                  }
                );
  
                const updateData = await updateResponse.json();
                if (!updateData.success) {
                  throw new Error(updateData.message || "Failed to update item");
                }
              }
            }
  
            SA.success("BOM updated successfully!", "Saved");
          }
        }
  
        onPatchCard?.(card.id, { processes, bom });
        onClose();
      } catch (e) {
        console.error("Save error:", e);
        SA.error(e.message || "Failed to save", "Save failed");
      }
    };
  
    const startEditBom = (idx) => {
      // If there's an unsaved edit in progress, revert it first
      if (editingBomIdx !== null && editingBomIdx !== idx && bomBackup) {
        updateAt(setBOM, editingBomIdx, bomBackup); // Restore the previous row
      }
  
      setEditingBomIdx(idx);
      setBomBackup({ ...bom[idx] }); // Backup the current row's data
    };
  
    // Function to cancel editing
    const cancelEditBom = () => {
      if (bomBackup && editingBomIdx !== null) {
        updateAt(setBOM, editingBomIdx, bomBackup); // Restore original
      }
      setEditingBomIdx(null);
      setBomBackup(null);
    };
  
    const saveBomRow = async (idx) => {
      const item = bom[idx];
  
      // CRITICAL: Validate item.id exists
      console.log("Saving BOM item:", item);
  
      if (!item || !item.id || item.id === "undefined") {
        SA.error("Cannot save - item ID is missing", "Invalid Item");
        return;
      }
  
      try {
        if (String(item.id).startsWith("bom-")) {
          // New item - POST logic
          let currentHeaderId = bomHeaderId;
  
          if (!currentHeaderId) {
            const headerResponse = await fetch(
              `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  order_id: card.orderDbId,
                  order_item_id: card.itemId,
                  // pwo_id: card.pwoid,
                  pwo_id: card.pwoid || card.pwo_id,
                  external_key: card.external_key,
                  notes: "",
                  version: 1,
                  items: [],
                }),
              }
            );
  
            const headerData = await headerResponse.json();
            if (!headerData.success) {
              throw new Error(
                headerData.message || "Failed to create BOM header"
              );
            }
  
            currentHeaderId = headerData.id;
            setBomHeaderId(headerData.id);
          }
  
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/${currentHeaderId}/items`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sku: item.sku,
                description: item.description,
                qty_per: item.qtyper,
                unit: item.unit,
                unit_price: item.unitprice,
                source: item.source,
                location: item.location,
                scrap_pct: item.scrappct,
              }),
            }
          );
  
          const data = await response.json();
  
          if (data.success) {
            // CRITICAL: Update with the correct ID field from response
            const newId = data.itemId || data.itemid || data.id;
            console.log("New item created with ID:", newId);
  
            updateAt(setBOM, idx, { ...item, id: newId });
            SA.success("BOM item added", "Success");
          } else {
            throw new Error(data.message || "Failed to add item");
          }
        } else {
          // Existing item - PATCH
          console.log("Updating existing item with ID:", item.id);
  
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/bom/items/${item.id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                sku: item.sku,
                description: item.description,
                qty_per: item.qtyper,
                unit: item.unit,
                unit_price: item.unitprice,
                source: item.source,
                location: item.location,
                scrap_pct: item.scrappct,
              }),
            }
          );
  
          const data = await response.json();
  
          if (data.success) {
            SA.success("BOM item updated", "Success");
          } else {
            throw new Error(data.message || "Failed to update item");
          }
        }
  
        setEditingBomIdx(null);
        setBomBackup(null);
      } catch (err) {
        console.error("Save BOM row error:", err);
        SA.error(`Failed to save BOM item: ${err.message}`, "Save failed");
      }
    };
  
    // ======= Remove Process  =======
    const removeProcess = async (idx) => {
      const item = processes[idx];
  
      if (String(item.id).startsWith("proc-")) {
        setProcesses((p) => p.filter((_, i) => i !== idx));
        return;
      }
  
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes/${item.id}`,
          {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
          }
        );
  
        const data = await response.json();
  
        if (data.success) {
          // Remove from UI after successful deletion
          setProcesses((p) => p.filter((_, i) => i !== idx));
          SA.success("Process deleted", "Success");
        } else {
          throw new Error(data.message || "Failed to delete");
        }
      } catch (err) {
        SA.error("Failed to delete process", err.message);
      }
    };
  
    // ======= Start/Cancel/Save Edit Functions =======
  
    const startEditProcess = (idx) => {
      // Auto-revert previous unsaved edits
      if (
        editingProcessIdx !== null &&
        editingProcessIdx !== idx &&
        processBackup
      ) {
        updateAt(setProcesses, editingProcessIdx, processBackup);
      }
  
      setEditingProcessIdx(idx);
      setProcessBackup({ ...processes[idx] });
    };
  
    const cancelEditProcess = () => {
      if (processBackup && editingProcessIdx !== null) {
        updateAt(setProcesses, editingProcessIdx, processBackup);
      }
      setEditingProcessIdx(null);
      setProcessBackup(null);
    };
  
    const saveProcessRow = async (idx) => {
      const item = processes[idx];
  
      try {
        if (String(item.id).startsWith("proc-")) {
          // New item - POST it
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                external_key: card.external_key,
                order_id: card.orderDbId,
                order_item_id: card.itemId,
                pwo_id: card.pwoid,
                // external_key: card.externalkey,
                sku: card.sku || null,
                step_no: item.step_no,
                name: item.name,
                person: item.person,
                type: item.type,
                machine_id: item.machine_id,
                est_minutes: item.est_minutes,
                due_date: item.due_date,
                cost: item.cost,
                notes: item.notes,
                department_id: item.department_id,
                department: card.department,
              }),
            }
          );
  
          const data = await response.json();
  
          if (data.success) {
            // Update with real ID from server
            updateAt(setProcesses, idx, { ...item, id: data.id });
            SA.success("Process added", "Success");
          }
        } else {
          // Existing item - PATCH it
          const response = await fetch(
            `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/order-processes/${item.id}`,
            {
              method: "PATCH",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                step_no: item.step_no,
                name: item.name,
                person: item.person,
                type: item.type,
                machine_id: item.machine_id,
                est_minutes: item.est_minutes,
                due_date: item.due_date,
                cost: item.cost,
                notes: item.notes,
                department_id: item.department_id,
              }),
            }
          );
  
          const data = await response.json();
  
          if (data.success) {
            SA.success("Process updated", "Success");
          }
        }
  
        setEditingProcessIdx(null);
        setProcessBackup(null);
      } catch (err) {
        SA.error("Failed to save process", err.message);
      }
    };
  
    const sendChat = async () => {
      const text = chatText.trim();
      if (!text) return;
      setChatText("");
      const localMsg = {
        id: `local-${Date.now()}`,
        text,
        when: new Date().toISOString(),
        author: "You",
      };
      setChat((c) => [...c, localMsg]);
      try {
        if (card.isStep) {
          await fetch(
            `${
              process.env.REACT_APP_NODEJS_SERVER_URL
            }/production/steps/${encodeURIComponent(card.id)}/messages`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ text }),
            }
          );
        }
      } catch {}
    };
  
    async function cancelJob(card, reason = "") {
      try {
        console.log(card.step_id);
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/cancel`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              step_id: card.step_id,
              reason: reason,
            }),
          }
        );
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }
  
        return await response.json();
      } catch (error) {
        console.error("Cancel Job API Error:", error);
        throw error;
      }
    }
  
    const cancelFlow = async () => {
      const result = await window.Swal.fire({
        title: "❌ Cancel This Job?",
        text: "This will permanently cancel the job and stop all production.",
        input: "textarea",
        inputLabel: "Cancellation reason (required):",
        inputPlaceholder: "Why is this job being cancelled?",
        showCancelButton: true,
        confirmButtonText: "Yes, Cancel Job",
        cancelButtonText: "Keep Job",
        icon: "warning",
        confirmButtonColor: "#dc2626",
        inputValidator: (value) => {
          if (!value || value.trim() === "") {
            return "Please provide a reason for cancellation";
          }
          return null;
        },
      });
  
      if (!result.isConfirmed) return;
      const reason = result.value.trim();
  
      if (card.isStep) {
        try {
          await cancelJob(card, reason);
          SA.success("Job cancelled successfully.", "Production stopped");
  
          onPatchCard(card.id, {
            cancelled: true,
  
            cancelledAt: new Date().toISOString(),
            lifecycle: [
              ...(card.lifecycle || []),
              {
                when: new Date().toLocaleString(),
                text: `Cancelled: ${reason}`,
              },
            ],
          });
        } catch (e) {
          SA.error(e.message || "Could not cancel job.", "Cancel failed");
        }
      }
    };
  
    async function reinstateJob(card, operator = "") {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/production/steps/reinstate`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              step_id: card.step_id,
              operator: operator,
            }),
          }
        );
  
        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP ${response.status}`);
        }
  
        return await response.json();
      } catch (error) {
        console.error("Reinstate Job API Error:", error);
        throw error;
      }
    }
  
    const reinstateFlow = async () => {
      const result = await window.Swal.fire({
        title: "Reinstate This Job?",
        text: "This will make the job active again and resume production.",
        input: "textarea",
        inputLabel: "Reason for reinstatement (optional):",
        inputPlaceholder: "Why is this job being reinstated?",
        showCancelButton: true,
        confirmButtonText: "Yes, Reinstate Job",
        cancelButtonText: "Cancel",
        icon: "question",
        confirmButtonColor: "#059669",
      });
  
      if (!result.isConfirmed) return;
      const note = result.value || "";
  
      onReinstateCard(card, note); //  Just call the prop
      onClose();
    };
  
    const hasSibs = siblingsAll.length > 1;
  
    const canJoinHere = (siblingsSameLane || []).length > 1; // same-lane siblings exist
  
    const aggregatedSizes =
      detailedCard?.Items?.map((i) => i.Size).filter(Boolean) || [];
    const aggregatedColors =
      detailedCard?.Items?.map((i) => i.Color).filter(Boolean) || [];
    const totalNetCostGoods = detailedCard?.Items?.reduce(
      (sum, i) => sum + (i.NetCostGoods || 0),
      0
    );
    const totalRetailCostGoods = detailedCard?.Items?.reduce(
      (sum, i) => sum + (i.RetailCostGoods || 0),
      0
    );
    const supplierName = detailedCard?.supplierName || "–";
    const category = detailedCard?.category || "–";
  
    const getEventColor = (type) => {
      switch (type) {
        case "SCHEDULE":
          return "bg-blue-500";
        case "START":
          return "bg-green-500";
        case "STATUS_CHANGE":
          return "bg-purple-500";
        case "COMPLETE":
          return "bg-emerald-500";
        case "PAUSE":
          return "bg-yellow-500";
        case "CANCEL":
          return "bg-red-500";
        default:
          return "bg-gray-500";
      }
    };
  
    const getEventBadge = (type) => {
      switch (type) {
        case "SCHEDULE":
          return "bg-blue-100 text-blue-800";
        case "START":
          return "bg-green-100 text-green-800";
        case "STATUS_CHANGE":
          return "bg-purple-100 text-purple-800";
        case "COMPLETE":
          return "bg-emerald-100 text-emerald-800";
        case "PAUSE":
          return "bg-yellow-100 text-yellow-800";
        case "CANCEL":
          return "bg-red-100 text-red-800";
        default:
          return "bg-gray-100 text-gray-800";
      }
    };
  
    const getStatusBadge = (status) => {
      switch (status) {
        case "Planned":
          return "bg-gray-100 text-gray-700";
        case "Scheduled":
          return "bg-blue-100 text-blue-700";
        case "In_Production":
          return "bg-cyan-100 text-cyan-700";
        case "QC":
          return "bg-yellow-100 text-yellow-700";
        case "Completed":
          return "bg-green-100 text-green-700";
        case "On_Hold":
          return "bg-red-100 text-red-700";
        default:
          return "bg-gray-100 text-gray-700";
      }
    };
  
    const parseSimpleNote = (note) => {
      if (!note) return "";
      return note
        .split("; ")
        .map((part) => {
          if (part.includes("=")) {
            const [key, value] = part.split("=");
            return `${key}: ${value}`;
          }
          return part;
        })
        .join(" • ");
    };
  
    const getStatusLabel = (statusKey) => {
      const status = STATUSES.find((s) => s.key === statusKey);
      return status ? status.label : statusKey?.replace("_", " ") || "Unknown";
    };
  
    // Parse detailed note data
    function parseDetailedNote(note) {
      if (!note) return {};
  
      const data = {};
      const pairs = note.split(";").map((p) => p.trim());
  
      pairs.forEach((pair) => {
        const [key, value] = pair.split("=").map((s) => s.trim());
        if (key && value) {
          data[key] = value;
        }
      });
  
      return data;
    }
  
    // Get event icon
    function getEventIcon(type) {
      const icons = {
        SCHEDULE: "bi-calendar-check",
        START: "bi-play-circle",
        PAUSE: "bi-pause-circle",
        RESUME: "bi-play-circle",
        COMPLETE: "bi-check-circle",
        STATUS_CHANGE: "bi-arrow-left-right",
      };
      return icons[type] || "bi-circle";
    }
  
    // Get event dot color
    function getEventDotColor(type) {
      const colors = {
        SCHEDULE: "bg-blue-500",
        START: "bg-green-500",
        PAUSE: "bg-orange-500",
        RESUME: "bg-green-500",
        COMPLETE: "bg-purple-500",
        STATUS_CHANGE: "bg-gray-500",
      };
      return colors[type] || "bg-gray-400";
    }
  
    // Calculate total time from first event to last
    function calculateTotalTime(events) {
      if (!events || events.length === 0) return "0h 0m";
  
      const first = new Date(events[0].at);
      const last = new Date(events[events.length - 1].at);
      const diffMs = last - first;
  
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
      return `${hours}h ${minutes}m`;
    }
  
    // Calculate time spent in a specific stage
    function calculateStageTime(events, stageName) {
      let totalMs = 0;
      let stageStart = null;
  
      for (let i = 0; i < events.length; i++) {
        const ev = events[i];
  
        // Entering the stage
        if (ev.to === stageName) {
          stageStart = new Date(ev.at);
        }
  
        // Leaving the stage
        if (ev.from === stageName && stageStart) {
          const stageEnd = new Date(ev.at);
          totalMs += stageEnd - stageStart;
          stageStart = null;
        }
      }
  
      // Still in stage (no exit event yet)
      if (stageStart) {
        totalMs += new Date() - stageStart;
      }
  
      const hours = Math.floor(totalMs / (1000 * 60 * 60));
      const minutes = Math.floor((totalMs % (1000 * 60 * 60)) / (1000 * 60));
  
      return `${hours}h ${minutes}m`;
    }
  
    // Calculate duration of a single event (time until next event)
    function calculateEventDuration(events, currentIndex) {
      if (currentIndex >= events.length - 1) return null;
  
      const current = new Date(events[currentIndex].at);
      const next = new Date(events[currentIndex + 1].at);
      const diffMs = next - current;
  
      const hours = Math.floor(diffMs / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  
      if (hours === 0 && minutes < 1) return "< 1m";
      if (hours === 0) return `${minutes}m`;
      return `${hours}h ${minutes}m`;
    }
  
    // Calculate estimated production cost based on time and machine rates
    function calculateProductionCost(events, card) {
      // Machine hourly rates (you can adjust these or fetch from DB)
      const machineRates = {
        Embroidery: 45.0,
        Screening: 35.0,
        "Direct to Film": 40.0,
        "Heat Press": 25.0,
        Default: 30.0,
      };
  
      let totalCost = 0;
      let productionStart = null;
  
      for (let i = 0; i < events.length; i++) {
        const ev = events[i];
  
        // Start production
        if (ev.to === "In_Production") {
          productionStart = new Date(ev.at);
        }
  
        // End production phase
        if (ev.from === "In_Production" && productionStart) {
          const endTime = new Date(ev.at);
          const hours = (endTime - productionStart) / (1000 * 60 * 60);
  
          // Get rate for this department
          const rate = machineRates[ev.department] || machineRates["Default"];
          totalCost += hours * rate;
  
          productionStart = null;
        }
      }
  
      // Still in production
      if (productionStart) {
        const hours = (new Date() - productionStart) / (1000 * 60 * 60);
        const rate = machineRates[card.department] || machineRates["Default"];
        totalCost += hours * rate;
      }
  
      return totalCost.toFixed(2);
    }
  
    // Get stage breakdown with percentages
    function getStageBreakdown(events) {
      let stages = ["Scheduled", "In_Production", "On_Hold"];
  
      if (SHOW_QC_LANE) {
        stages = ["Scheduled", "In_Production", "QC", "On_Hold"];
      }
  
      const stageTimes = {};
      let totalMs = 0;
  
      // Calculate time for each stage
      stages.forEach((stage) => {
        let stageMs = 0;
        let stageStart = null;
  
        for (let i = 0; i < events.length; i++) {
          const ev = events[i];
  
          if (ev.to === stage) {
            stageStart = new Date(ev.at);
          }
  
          if (ev.from === stage && stageStart) {
            stageMs += new Date(ev.at) - stageStart;
            stageStart = null;
          }
        }
  
        // Still in stage
        if (stageStart) {
          stageMs += new Date() - stageStart;
        }
  
        stageTimes[stage] = stageMs;
        totalMs += stageMs;
      });
  
      // Build breakdown array
      const breakdown = stages
        .map((stage) => {
          const ms = stageTimes[stage];
          const hours = Math.floor(ms / (1000 * 60 * 60));
          const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
          const percentage = totalMs > 0 ? (ms / totalMs) * 100 : 0;
  
          return {
            name: stage,
            label: getStatusLabel(stage),
            duration: `${hours}h ${minutes}m`,
            percentage: percentage.toFixed(1),
            color: getStageColor(stage),
          };
        })
        .filter((s) => s.percentage > 0);
  
      return breakdown;
    }
  
    // Get progress bar color for each stage
    function getStageColor(stage) {
      const colors = {
        Scheduled: "bg-blue-500",
        In_Production: "bg-cyan-500",
        QC: "bg-yellow-500",
        On_Hold: "bg-orange-500",
        Completed: "bg-green-500",
      };
      return colors[stage] || "bg-gray-500";
    }
  
  return (
    <div>
      {/* ... all your modal JSX ... */}
      <div style={modal.overlay} onClick={onClose}>
           <div style={modal.card} onClick={(e) => e.stopPropagation()}>
             <div style={modal.header}>
               <div
                 style={{
                   display: "flex",
                   alignItems: "center",
                   gap: 8,
                   flexWrap: "wrap",
                 }}
               >
                 <h3 style={{ margin: 0 }}>
                   Order No. - {card.orderNumber || card.title || "Job Card"}
                 </h3>
                 {card.cancelled && <span style={ui.cancelBadge}>CANCELLED</span>}
                 {card.status && (
                   <span style={ui.statusPill}>
                     {card.status.replace(/_/g, " ")}
                   </span>
                 )}
                 {hasSibs && <span style={ui.badge}>Batch</span>}
               </div>
     
               {/* ACTIONS (Split = Scheduled-only; Join = any status if same-lane siblings) */}
               <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                 {!card.cancelled && (
                   <>
                     {card.status === "Scheduled" && (
                       <button
                         style={ui.linkBtn}
                         onClick={() => onOpenSplit?.(card)}
                       >
                         Split…
                       </button>
                     )}
                     {canJoinHere && (
                       <button style={ui.linkBtn} onClick={() => onOpenJoin?.(card)}>
                         Join…
                       </button>
                     )}
                   </>
                 )}
     
                 {!card.cancelled ? (
                   <button style={ui.btnDanger} onClick={cancelFlow}>
                     Cancel
                   </button>
                 ) : (
                   <button style={ui.btnWarn} onClick={reinstateFlow}>
                     Reinstate
                   </button>
                 )}
                 <button style={ui.btnPrimary} onClick={() => setTab("decoratorNetwork")}>
                   Find Decorator
                 </button>
                 <button style={ui.btnGhost} onClick={onClose}>
                   Close
                 </button>
               </div>
             </div>
     
             <div className="border-b border-gray-200 bg-white">
               <nav className="flex -mb-px overflow-x-auto" aria-label="Tabs">
                 {[
                   { id: "overview", label: "Details", icon: "bi-info-circle" },
                   // { id: "overview2", label: "Details", icon: "bi-info-circle" },
                 { id: "processes", label: "Processes", icon: "bi-list-check" },
                 { id: "bom", label: "BOM", icon: "bi-box-seam" },
                 { id: "proofing", label: "Proofing", icon: "bi-file-earmark-check" },
                 { id: "decoratorNetwork", label: "Decorator Network", icon: "bi-diagram-3" },
                 { id: "chat", label: "Chat", icon: "bi-chat-dots" },
                 { id: "activity", label: "Activity", icon: "bi-clock-history" },
                 { id: "cost", label: "Cost", icon: "bi-calculator" },
                 ].map((t) => (
                   <button
                     key={t.id}
                     onClick={() => setTab(t.id)}
                     className={`
               group inline-flex items-center gap-2 px-6 py-3 
               border-b-2 font-medium text-sm transition-all duration-200
               ${
                 tab === t.id
                   ? "border-blue-500 text-blue-600"
                   : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
               }
             `}
                   >
                     <i
                       className={`${t.icon} ${
                         tab === t.id
                           ? "text-blue-600"
                           : "text-gray-400 group-hover:text-gray-600"
                       }`}
                     ></i>
                     <span>{t.label}</span>
                   </button>
                 ))}
               </nav>
             </div>
     
             {tab === "proofing" && (
               <div className="p-6 bg-gray-50">
                 <div className="mb-5">
                   <h3 className="text-xl font-semibold text-gray-900 mb-1">Proofing</h3>
                   <p className="text-sm text-gray-500">
                     Customer proofs require approval. Files for production are internal source assets and do not require customer approval.
                   </p>
                 </div>

                 <div className="bg-white rounded-lg border border-gray-200 p-5 mb-5">
                   <div className="d-flex align-items-center justify-content-between mb-3">
                     <div>
                       <h4 className="text-sm font-semibold text-gray-900 mb-0">Customer proof files</h4>
                       <p className="text-xs text-gray-500 mb-0">The artwork the customer reviewed or will review.</p>
                     </div>
                   </div>
                   <div className="d-flex flex-column gap-2">
                     {(Array.isArray(orderMeta?.proofsNormalized) ? orderMeta.proofsNormalized : []).map((proof, index) => {
                       const fileUrl = proof?.filePath || proof?.previewUrl || proof?.proofFilePath || "";
                       const fileName =
                         proof?.fileName ||
                         (proof?.poNumber ? `${proof.poNumber} Proof` : "") ||
                         (fileUrl ? String(fileUrl).split("/").pop() : `Proof ${index + 1}`);
                       if (!fileUrl) return null;
                       return (
                         <a
                           key={proof?.id || fileUrl || index}
                           href={fileUrl}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="d-flex align-items-center justify-content-between px-3 py-2 border rounded text-decoration-none"
                         >
                           <span className="d-flex align-items-center gap-2">
                             <i className="bi bi-file-earmark-check text-primary"></i>
                             <span>{fileName}</span>
                           </span>
                           <span className="text-xs text-muted">
                             {proof?.approvalState ? `Approval: ${proof.approvalState}` : "Proof"}
                           </span>
                         </a>
                       );
                     })}
                     {(!Array.isArray(orderMeta?.proofsNormalized) || orderMeta.proofsNormalized.filter((proof) => proof?.filePath || proof?.previewUrl || proof?.proofFilePath).length === 0) && (
                       <div className="text-sm text-gray-500">No customer proof files are attached.</div>
                     )}
                   </div>
                 </div>

                 <div className="bg-white rounded-lg border border-gray-200 p-5">
                   <div className="mb-3">
                     <h4 className="text-sm font-semibold text-gray-900 mb-0">Files for Production</h4>
                     <p className="text-xs text-gray-500 mb-0">
                       AI, EPS and other source files supplied for production. These files are not part of customer approval.
                     </p>
                   </div>
                   <div className="d-flex flex-column gap-2">
                     {(Array.isArray(orderMeta?.productionFiles) ? orderMeta.productionFiles : []).map((file, index) => {
                       const fileUrl = file?.filePath || file?.sourceFilePath || file?.downloadUrl || file?.url || "";
                       const fileName =
                         file?.fileName ||
                         file?.sourceFileName ||
                         (fileUrl ? String(fileUrl).split("/").pop() : `Production file ${index + 1}`);
                       if (!fileUrl) return null;
                       return (
                         <a
                           key={file?.sourceFileId || fileUrl || index}
                           href={fileUrl}
                           target="_blank"
                           rel="noopener noreferrer"
                           className="d-flex align-items-center justify-content-between px-3 py-2 border rounded text-decoration-none"
                         >
                           <span className="d-flex align-items-center gap-2">
                             <i className="bi bi-file-earmark-code text-secondary"></i>
                             <span>{fileName}</span>
                           </span>
                           <span className="text-xs text-muted">
                             {file?.sourceProvider || file?.sourceType || file?.source || "Production"}
                           </span>
                         </a>
                       );
                     })}
                     {(!Array.isArray(orderMeta?.productionFiles) || orderMeta.productionFiles.length === 0) && (
                       <div className="text-sm text-gray-500">No production-only files are attached yet.</div>
                     )}
                   </div>
                 </div>
               </div>
             )}

             {tab === "overview" && (
               <>
                 {/* Enterprise-Grade Professional Styles */}
                 <style>{`
                   @keyframes spin {
                     to { transform: rotate(360deg); }
                   }
                   
                   .overview-metric {
                     transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
                     border-left: 2px solid #dee2e6;
                   }
                   
                   .overview-metric:hover {
                     border-left-color: #0d6efd;
                     box-shadow: 0 0.125rem 0.375rem rgba(0, 0, 0, 0.075);
                   }
                   
                   .overview-card {
                     transition: box-shadow 0.2s ease;
                   }
                   
                   .overview-card:hover {
                     box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.08);
                   }
                   
                   .overview-table {
                     font-size: 0.875rem;
                   }
                   
                   .overview-table thead th {
                     background-color: #f8f9fa;
                     font-weight: 600;
                     font-size: 0.75rem;
                     text-transform: uppercase;
                     letter-spacing: 0.5px;
                     color: #6c757d;
                     border-bottom: 2px solid #dee2e6;
                     padding: 0.75rem;
                   }
                   
                   .overview-table tbody td {
                     padding: 0.75rem;
                     vertical-align: middle;
                     border-bottom: 1px solid #f0f0f0;
                   }
                   
                   .overview-table tbody tr:hover {
                     background-color: #f8f9fa;
                   }
                   
                   .section-header {
                     font-size: 0.8125rem;
                     font-weight: 600;
                     color: #495057;
                     text-transform: uppercase;
                     letter-spacing: 0.5px;
                     margin-bottom: 0.75rem;
                     padding-bottom: 0.5rem;
                     border-bottom: 1px solid #dee2e6;
                   }
                   
                   .info-row {
                     display: flex;
                     justify-content: space-between;
                     padding: 0.625rem 0;
                     border-bottom: 1px solid #f0f0f0;
                   }
                   
                   .info-row:last-child {
                     border-bottom: none;
                   }
                   
                   .info-label {
                     font-size: 0.8125rem;
                     color: #6c757d;
                     font-weight: 500;
                   }
                   
                   .info-value {
                     font-size: 0.8125rem;
                     color: #212529;
                     font-weight: 600;
                     text-align: right;
                   }
                 `}</style>
     
                 <div
                   style={{
                     padding: "1.5rem",
                     backgroundColor: "#f5f6f8",
                     // minHeight: "400px",
                   }}
                 >
                   {/* ========== LOADING STATE ========== */}
                   {modalLoading && (
                     <div
                       className="d-flex flex-column align-items-center justify-content-center"
                       style={{ padding: "5rem 1.5rem" }}
                     >
                       <div
                         style={{
                           width: "40px",
                           height: "40px",
                           border: "3px solid #e9ecef",
                           borderTopColor: "#0d6efd",
                           borderRadius: "50%",
                           animation: "spin 1s linear infinite",
                         }}
                       />
                       <div
                         className="mt-3"
                         style={{
                           color: "#6c757d",
                           fontSize: "0.875rem",
                           fontWeight: 500,
                         }}
                       >
                         Loading order details...
                       </div>
                     </div>
                   )}
     
                   {/* ========== MAIN CONTENT ========== */}
                   {!modalLoading && detailedCard && (
                     <>
                       {/* STOP SIGNAL ALERT */}
                       {card.meta?.stopSignal && (
                         <div
                           className="alert alert-danger d-flex align-items-center mb-3"
                           style={{ fontSize: "0.875rem" }}
                         >
                           <i className="bi bi-exclamation-triangle-fill me-2"></i>
                           <strong>Production Stopped:</strong>
                           <span className="ms-2">
                             {card.meta?.stopLabel || "Production halted"}
                           </span>
                         </div>
                       )}
     
                       {/* Key Metrics Row */}
                       <div className="row g-3 mb-3">
                         {/* Order Number */}
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-receipt text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Order No.
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "1.25rem",
                                   fontWeight: 700,
                                   color: "#212529",
                                 }}
                               >
                                 {/* {detailedCard.OrderId ||
                                   card.meta?.orderNumber ||
                                   "–"} */}
     
                                 {detailedCard.OrderId ||
                                   orderMeta?.orderNumber ||
                                   "–"}
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Project */}
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-folder2-open text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Project
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "0.875rem",
                                   fontWeight: 600,
                                   color: "#212529",
                                 }}
                                 className="text-truncate"
                               >
                                 {/* {detailedCard.ProjectName ||
                                   card.meta?.projectName ||
                                   "Untitled"} */}
     
                                 {detailedCard.ProjectName ||
                                   orderMeta?.projectName ||
                                   "Untitled"}
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Quantity */}
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-box-seam text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Quantity
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "1.25rem",
                                   fontWeight: 700,
                                   color: "#212529",
                                 }}
                               >
                                 {detailedCard.Items?.reduce(
                                   (sum, item) => sum + (item.Quantity || 0),
                                   0
                                 ) || 0}
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* In-Hands Date */}
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-calendar3 text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Decorator PO In-Hands
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "0.875rem",
                                   fontWeight: 600,
                                   color: "#212529",
                                 }}
                               >
                                 {formatProductionBusinessDate(detailedCard.decoratorPoInHandsDate)}
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Total Cost */}
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-currency-dollar text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Total Cost
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "1.125rem",
                                   fontWeight: 700,
                                   color: "#212529",
                                 }}
                               >
                                 $
                                 {(
                                   detailedCard.Items?.reduce(
                                     (sum, item) => sum + (item.Subtotal || 0),
                                     0
                                   ) || 0
                                 ).toFixed(2)}
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Status */}
                         {/* <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-check-circle text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Status
                                 </span>
                               </div>
                               <div
                                 style={{
                                   fontSize: "0.875rem",
                                   fontWeight: 600,
                                   color: "#212529",
                                 }}
                                 className="text-truncate"
                               >
                                 {detailedCard.OrderStatus?.SO ||
                                   card.meta?.salesOrderStatus ||
                                   "Pending"}<br/>
     
                                    {detailedCard.OrderStatus?.PO ||
                                   card.meta?.salesOrderStatus ||
                                   "Pending"}
                               </div>
                             </div>
                           </div>
                         </div> */}
     
                         <div className="col-xl-2 col-lg-3 col-md-4 col-sm-6">
                           <div
                             className="card overview-metric border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div className="card-body" style={{ padding: "1rem" }}>
                               <div className="d-flex align-items-center mb-2">
                                 <i
                                   className="bi bi-check-circle text-primary me-2"
                                   style={{ fontSize: "1.125rem" }}
                                 ></i>
                                 <span
                                   style={{
                                     fontSize: "0.6875rem",
                                     fontWeight: 600,
                                     textTransform: "uppercase",
                                     letterSpacing: "0.5px",
                                     color: "#6c757d",
                                   }}
                                 >
                                   Status
                                 </span>
                               </div>
     
                               <div
                                 style={{
                                   fontSize: "0.875rem",
                                   fontWeight: 600,
                                   color: "#212529",
                                   lineHeight: "1.4",
                                 }}
                               >
                                 {/* SO Status - with individual truncate */}
                                 <div
                                   className="text-truncate"
                                   title={
                                     detailedCard.OrderStatus?.SO ||
                                     card.meta?.salesOrderStatus ||
                                     "Pending"
                                   }
                                 >
                                   {detailedCard.OrderStatus?.SO ||
                                     card.meta?.salesOrderStatus ||
                                     "Pending"}
                                 </div>
     
                                 {/* PO Status - with word break for wrapping */}
                                 <div
                                   style={{
                                     wordBreak: "break-word",
                                     fontSize: "0.8125rem",
                                     marginTop: "0.25rem",
                                   }}
                                 >
                                   {detailedCard.OrderStatus?.PO ||
                                     card.meta?.salesOrderStatus ||
                                     "Pending"}
                                 </div>
                               </div>
                             </div>
                           </div>
                         </div>
                       </div>
     
                       {/* Information Cards Grid */}
                       <div className="row g-3 mb-3">
                         {/* Project Information */}
                         <div className="col-lg-4 col-md-6">
                           <div
                             className="card overview-card border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div
                               className="card-body"
                               style={{ padding: "1.25rem" }}
                             >
                               <h6 className="section-header">
                                 <i className="bi bi-info-circle me-2"></i>Project
                                 Details
                               </h6>
                               <div className="info-row">
                                 <span className="info-label">Project name</span>
                                 <span className="info-value">
                                   {detailedCard.ProjectName ||
                                     orderMeta?.projectName ||
                                     "Untitled"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Order Number</span>
                                 <span
                                   className="info-value"
                                   style={{ fontWeight: 700 }}
                                 >
                                   {detailedCard.OrderId ||
                                     orderMeta?.orderNumber ||
                                     "–"}
                                 </span>
                               </div>
     
                               <div className="info-row">
                                 <span className="info-label">Created Date</span>
                                 <span className="info-value">
                                   {detailedCard.createdAt
                                     ? DATE_FMT(detailedCard.createdAt)
                                     : "–"}
                                 </span>
                               </div>
     
                               {/* Purchase Orders */}
     
                               <div className="info-row">
                                 <div>
                                   <div
                                     style={{
                                       fontSize: "0.75rem",
                                       fontWeight: 600,
                                       color: "#6c757d",
                                       marginBottom: "0.5rem",
                                       textTransform: "uppercase",
                                       letterSpacing: "0.5px",
                                     }}
                                   >
                                     Purchase Orders
                                     {orderMeta?.purchaseOrders?.length > 0 &&
                                       ` (${orderMeta.purchaseOrders.length})`}
                                   </div>
     
                                   {orderMeta?.purchaseOrders &&
                                   orderMeta.purchaseOrders.length > 0 ? (
                                     <div className="d-flex flex-column gap-2">
                                       {orderMeta.purchaseOrders.map((po, idx) => (
                                         <div
                                           key={idx}
                                           style={{
                                             padding: "0.5rem",
                                             backgroundColor: "#f8f9fa",
                                             border: "1px solid #e9ecef",
                                             borderRadius: "0.25rem",
                                             fontSize: "0.8125rem",
                                           }}
                                         >
                                           <div className="d-flex justify-content-between align-items-center">
                                             <span
                                               className="font-monospace"
                                               style={{ fontWeight: 600 }}
                                             >
                                               {po.poNumber}
                                             </span>
                                             {/* <span
                     // className="badge badgeNew bg-secondary"
                                       className="info-value"
                     style={{ fontSize: "0.65rem" }}
                   >
                     ID: {po.id}
                   </span> */}
                                           </div>
                                           <div
                                             style={{
                                               fontSize: "0.75rem",
                                               color: "#6c757d",
                                               marginTop: "0.25rem",
                                             }}
                                           >
                                             Status: {po.supplierStatus || "–"}
                                           </div>
                                         </div>
                                       ))}
                                     </div>
                                   ) : (
                                     <div
                                       style={{
                                         fontSize: "0.8125rem",
                                         color: "#9ca3af",
                                         fontStyle: "italic",
                                       }}
                                     >
                                       –
                                     </div>
                                   )}
                                 </div>
                               </div>
                               {/* Global Sales Order */}
     
                               {/* Global Sales Order */}
                               {/* <div> */}
                               <div className="info-row">
                                 <span className="info-label">Global SO</span>
                                 <span
                                   className="info-value"
                                   style={{
                                     color: orderMeta?.salesOrderGlobal?.trim()
                                       ? "#212529"
                                       : "#9ca3af",
                                   }}
                                 >
                                   {orderMeta?.salesOrderGlobal?.trim() || "–"}
                                 </span>
                               </div>
                               {/* </div> */}
     
                               {/* Production Active Flag */}
     
                               <div className="info-row">
                                 <span className="info-label">
                                   Production Active
                                 </span>
                                 <span
                                   // className={`badge ${
                                   //   orderMeta?.activeForProduction
                                   //     ? "bg-success"
                                   //     : "bg-secondary"
                                   // }`}
                                   className="info-value"
                                   style={{ fontSize: "0.6875rem" }}
                                 >
                                   {orderMeta?.activeForProduction ? "Yes" : "No"}
                                 </span>
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Product Summary */}
                         <div className="col-lg-4 col-md-6">
                           <div
                             className="card overview-card border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div
                               className="card-body"
                               style={{ padding: "1.25rem" }}
                             >
                               {/* <div className="d-flex justify-content-between align-items-center mb-3"> */}
                               <div className="d-flex justify-content-between align-items-center mb-2">
                                 {/* <h6 className="section-header mb-0 pb-0 border-0">
                                   <i className="bi bi-box-seam me-2"></i>Product
                                   Summary
                                 </h6> */}
     
                                 <h6 className="section-header mb-0 pb-0 border-0">
                                   <i className="bi bi-box-seam me-2"></i>Product
                                   Summary
                                 </h6>
                                 {/* <span
                                   className="badge badgeNew bg-primary"
                                   style={{ fontSize: "0.6875rem" }}
                                 >
                                   {detailedCard.Items?.length || 0}{" "}
                                   {detailedCard.Items?.length === 1
                                     ? "Item"
                                     : "Items"}
                                 </span> */}
                               </div>
     
                               <div
                                 style={{
                                   borderBottom: "1px solid #dee2e6",
                                   marginBottom: "0.75rem",
                                 }}
                               ></div>
                               <div className="info-row">
                                 <span className="info-label">Sizes</span>
                                 <span className="info-value">
                                   {[
                                     ...new Set(
                                       detailedCard.Items?.map((i) => i.Size).filter(
                                         Boolean
                                       )
                                     ),
                                   ].join(", ") || "–"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Colors</span>
                                 <span className="info-value">
                                   {[
                                     ...new Set(
                                       detailedCard.Items?.map(
                                         (i) => i.Color
                                       ).filter(Boolean)
                                     ),
                                   ].join(", ") || "–"}
                                 </span>
                               </div>
                               {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                               <div className="info-row">
                                 <span className="info-label">Product Subtotal</span>
                                 <span
                                   className="info-value"
                                   style={{ fontWeight: 700 }}
                                 >
                                   $
                                   {(
                                     detailedCard.Items?.reduce(
                                       (sum, i) => sum + (i.Subtotal || 0),
                                       0
                                     ) || 0
                                   ).toFixed(2)}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Tax Amount</span>
                                 <span className="info-value">
                                   $
                                   {(
                                     detailedCard.Items?.reduce(
                                       (sum, i) =>
                                         sum +
                                         Number(
                                           i.TaxAmount ??
                                             i.taxAmount ??
                                             i.Tax ??
                                             0
                                         ),
                                       0
                                     ) || 0
                                   ).toFixed(2)}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Currency</span>
                                 <span className="info-value">
                                   {detailedCard.Items?.[0]?.CurrencyCode || "–"}
                                 </span>
                               </div>
                               {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                               <div className="info-row">
                                 <span className="info-label">Supplier</span>
                                 <span className="info-value">
                                   {detailedCard.Items?.[0]?.SupplierName || "–"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Supplier ID</span>
                                 <span
                                   className="info-value font-monospace"
                                   style={{ fontSize: "0.75rem" }}
                                 >
                                   {detailedCard.Items?.[0]?.SupplierId || "–"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Source</span>
                                 <span className="info-value text-uppercase">
                                   {detailedCard.Items?.[0]?.Source || "–"}
                                 </span>
                               </div>
                             </div>
                           </div>
                         </div>
     
                         {/* Schedule & Status */}
                         <div className="col-lg-4 col-md-6">
                           <div
                             className="card overview-card border-0 h-100"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div
                               className="card-body"
                               style={{ padding: "1.25rem" }}
                             >
                               <h6 className="section-header">
                                 <i className="bi bi-clock-history me-2"></i>Schedule
                                 & Status
                               </h6>
                               <div className="info-row">
                                 <span className="info-label">Customer</span>
                                 <span className="info-value">
                                   {/* {detailedCard.CustomerName ||
                                     card.meta?.customerName ||
                                     "–"} */}
     
                                   {detailedCard.CustomerName ||
                                     orderMeta?.customerName ||
                                     "–"}
                                 </span>
                               </div>
     
                               {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                               <div className="info-row">
                                 <span className="info-label">Customer In-Hands</span>
                                 <span className="info-value">
                                   {detailedCard.inHandsDate
                                     ? formatProductionBusinessDate(detailedCard.inHandsDate)
                                     : "–"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Scheduled</span>
                                 <span className="info-value">
                                   {detailedCard.plannedStart
                                     ? DATE_FMT(detailedCard.plannedStart)
                                     : "–"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Machine</span>
                                 <span className="info-value">
                                   {detailedCard.machine ||
                                     (hasSibs ? "Multiple" : "–")}
                                 </span>
                               </div>
                               {/* <hr style={{ margin: "1rem 0", borderColor: "#e9ecef" }} /> */}
                               <div className="info-row">
                                 <span className="info-label">Sales Order</span>
                                 <span className="info-value">
                                   {/* {detailedCard.OrderStatus?.SO ||
                                     card.meta?.salesOrderStatus ||
                                     "–"} */}
     
                                   {detailedCard.OrderStatus?.SO ||
                                     orderMeta?.salesOrderStatus ||
                                     "Pending"}
                                 </span>
                               </div>
                               <div className="info-row">
                                 <span className="info-label">Purchase Order</span>
                                 <span className="info-value">
                                   {/* {detailedCard.OrderStatus?.PO ||
                                     card.meta?.purchaseOrderStatus ||
                                     "–"} */}
     
                                   {detailedCard.OrderStatus?.PO ||
                                     orderMeta?.purchaseOrderStatus}
                                 </span>
                               </div>
                             </div>
                           </div>
                         </div>
                       </div>
     
                       {/* Costing Information */}
                       {orderCosting && (
                         <div
                           className="card overview-card border-0 mb-3"
                           style={{ backgroundColor: "#ffffff" }}
                         >
                           <div className="card-body" style={{ padding: "1.25rem" }}>
                             <h6 className="section-header">
                               <i className="bi bi-calculator me-2"></i>Cost
                               Breakdown
                             </h6>
                             <div className="row">
                               <div className="col-md-3 col-sm-6">
                                 <div
                                   className="text-center p-3"
                                   style={{
                                     backgroundColor: "#f8f9fa",
                                     borderRadius: "0.25rem",
                                   }}
                                 >
                                   <div
                                     style={{
                                       fontSize: "0.75rem",
                                       color: "#6c757d",
                                       marginBottom: "0.5rem",
                                       fontWeight: 600,
                                     }}
                                   >
                                     BOM Cost
                                   </div>
                                   <div
                                     style={{
                                       fontSize: "1.125rem",
                                       fontWeight: 700,
                                       color: "#212529",
                                     }}
                                   >
                                     {/* $
                                     {(card.costing.bomCostSubtotal || 0).toFixed(2)} */}
                                     $
                                     {(orderCosting.bomCostSubtotal || 0).toFixed(2)}
                                   </div>
                                 </div>
                               </div>
                               <div className="col-md-3 col-sm-6">
                                 <div
                                   className="text-center p-3"
                                   style={{
                                     backgroundColor: "#f8f9fa",
                                     borderRadius: "0.25rem",
                                   }}
                                 >
                                   <div
                                     style={{
                                       fontSize: "0.75rem",
                                       color: "#6c757d",
                                       marginBottom: "0.5rem",
                                       fontWeight: 600,
                                     }}
                                   >
                                     Process Cost
                                   </div>
                                   <div
                                     style={{
                                       fontSize: "1.125rem",
                                       fontWeight: 700,
                                       color: "#212529",
                                     }}
                                   >
                                     {/* $
                                     {(
                                       card.costing.processCostSubtotal || 0
                                     ).toFixed(2)} */}
                                     $
                                     {(
                                       orderCosting.processCostSubtotal || 0
                                     ).toFixed(2)}
                                   </div>
                                 </div>
                               </div>
                               <div className="col-md-3 col-sm-6">
                                 <div
                                   className="text-center p-3"
                                   style={{
                                     backgroundColor: "#f8f9fa",
                                     borderRadius: "0.25rem",
                                   }}
                                 >
                                   <div
                                     style={{
                                       fontSize: "0.75rem",
                                       color: "#6c757d",
                                       marginBottom: "0.5rem",
                                       fontWeight: 600,
                                     }}
                                   >
                                     Est. Minutes
                                   </div>
                                   <div
                                     style={{
                                       fontSize: "1.125rem",
                                       fontWeight: 700,
                                       color: "#212529",
                                     }}
                                   >
                                     {/* {card.costing.estMinutesTotal || 0} min */}
                                     {orderCosting.estMinutesTotal || 0} min
                                   </div>
                                 </div>
                               </div>
                               <div className="col-md-3 col-sm-6">
                                 <div
                                   className="text-center p-3"
                                   style={{
                                     backgroundColor: "#fff3cd",
                                     borderRadius: "0.25rem",
                                     border: "1px solid #ffc107",
                                   }}
                                 >
                                   <div
                                     style={{
                                       fontSize: "0.75rem",
                                       color: "#856404",
                                       marginBottom: "0.5rem",
                                       fontWeight: 700,
                                     }}
                                   >
                                     Grand Total
                                   </div>
                                   <div
                                     style={{
                                       fontSize: "1.125rem",
                                       fontWeight: 700,
                                       color: "#856404",
                                     }}
                                   >
                                     {/* ${(card.costing.grandTotalCost || 0).toFixed(2)} */}
                                     ${(orderCosting.grandTotalCost || 0).toFixed(2)}
                                   </div>
                                 </div>
                               </div>
                             </div>
                           </div>
                         </div>
                       )}
     
                       {/* Department Progress */}
                       {/* {card.meta?.eligible_departments &&
                         card.meta.eligible_departments.length > 0 && ( */}
                       {orderMeta?.eligible_departments &&
                         orderMeta.eligible_departments.length > 0 && (
                           <div
                             className="card overview-card border-0 mb-3"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div
                               className="card-body"
                               style={{ padding: "1.25rem" }}
                             >
                               <h6 className="section-header">
                                 <i className="bi bi-diagram-3 me-2"></i>Department
                                 Progress
                               </h6>
                               <div className="row g-3">
                                 {/* {card.meta.eligible_departments.map((dept, idx) => ( */}
                                 {orderMeta.eligible_departments.map((dept, idx) => (
                                   <div
                                     key={idx}
                                     className="col-lg-3 col-md-4 col-sm-6"
                                   >
                                     <div
                                       style={{
                                         padding: "0.875rem 1rem",
                                         backgroundColor:
                                           dept.status === "Completed"
                                             ? "#d1fae5"
                                             : dept.status
                                             ? "#fef3c7"
                                             : "#f8f9fa",
                                         border: `1px solid ${
                                           dept.status === "Completed"
                                             ? "#10b981"
                                             : dept.status
                                             ? "#f59e0b"
                                             : "#e9ecef"
                                         }`,
                                         borderRadius: "0.25rem",
                                       }}
                                     >
                                       <div className="d-flex align-items-start justify-content-between mb-2">
                                         <div className="d-flex align-items-center">
                                           {dept.status === "Completed" ? (
                                             <i
                                               className="bi bi-check-circle-fill text-success me-2"
                                               style={{ fontSize: "1.25rem" }}
                                             ></i>
                                           ) : dept.status ? (
                                             <i
                                               className="bi bi-arrow-repeat text-warning me-2"
                                               style={{ fontSize: "1.25rem" }}
                                             ></i>
                                           ) : (
                                             <i
                                               className="bi bi-circle text-secondary me-2"
                                               style={{ fontSize: "1.25rem" }}
                                             ></i>
                                           )}
                                           <div>
                                             <div
                                               style={{
                                                 fontSize: "0.875rem",
                                                 fontWeight: 600,
                                                 color: "#212529",
                                               }}
                                             >
                                               {dept.name}
                                             </div>
                                             <div
                                               style={{
                                                 fontSize: "0.7rem",
                                                 color: "#6c757d",
                                                 fontFamily: "monospace",
                                               }}
                                             >
                                               ID: {dept.id}
                                             </div>
                                           </div>
                                         </div>
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color:
                                             dept.status === "Completed"
                                               ? "#065f46"
                                               : dept.status
                                               ? "#92400e"
                                               : "#6c757d",
                                           fontWeight: 600,
                                         }}
                                       >
                                         {dept.status || "Pending"}
                                       </div>
                                     </div>
                                   </div>
                                 ))}
                               </div>
                             </div>
                           </div>
                         )}
     
                       {/* Products with Decorations */}
                       {detailedCard.Items?.map((item, itemIndex) => {
                         const decorationCount =
                           item.decorationCount ?? item.decorations?.length ?? 0;
                         const totalCharges =
                           item.chargeCount ??
                           item.decorations?.reduce(
                             (sum, deco) => sum + (deco.Charge?.length || 0),
                             0
                           ) ??
                           0;
     
                         return (
                           <div
                             key={item.id}
                             className="card overview-card border-0 mb-3"
                             style={{ backgroundColor: "#ffffff" }}
                           >
                             <div
                               className="card-body"
                               style={{ padding: "1.25rem" }}
                             >
                               {/* Product Header */}
                               <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                                 <h6 className="section-header mb-0 pb-0 border-0">
                                   <i className="bi bi-box me-2"></i>
                                   Product
                                   {/* {itemIndex + 1} */}: {item.Name || "–"}
                                 </h6>
     
                                 <div
                                   className="d-flex gap-2 flex-wrap"
                                   style={{
                                     gap: "0.5rem",
                                     flexWrap: "wrap",
                                     alignItems: "center",
                                   }}
                                 >
                                   <span
                                     className="badge badgeNew bg-secondary"
                                     style={{
                                       fontSize: "0.6875rem",
                                       whiteSpace: "nowrap",
                                       padding: "0.35rem 0.65rem",
                                     }}
                                   >
                                     ID: {item.id}
                                   </span>
                                   <span
                                     className="badge badgeNew bg-primary"
                                     style={{
                                       fontSize: "0.6875rem",
                                       whiteSpace: "nowrap",
                                       padding: "0.35rem 0.65rem",
                                     }}
                                   >
                                     Qty: {item.Quantity}
                                   </span>
                                 {decorationCount > 0 && (
                                   <span
                                     className="badge badgeNew bg-info"
                                     style={{
                                       fontSize: "0.6875rem",
                                       whiteSpace: "nowrap",
                                       padding: "0.35rem 0.65rem",
                                     }}
                                   >
                                     <i className="bi bi-palette me-1"></i>
                                     {decorationCount} Decoration
                                     {decorationCount !== 1 ? "s" : ""}
                                   </span>
                                 )}
                                 {totalCharges > 0 && (
                                   <span
                                     className="badge badgeNew bg-secondary"
                                     style={{
                                       fontSize: "0.6875rem",
                                       whiteSpace: "nowrap",
                                       padding: "0.35rem 0.65rem",
                                     }}
                                   >
                                     <i className="bi bi-tags me-1"></i>
                                     {totalCharges} Charge
                                     {totalCharges !== 1 ? "s" : ""}
                                   </span>
                                 )}
                                   {item.isDeleted && (
                                     <span
                                       className="badge badgeNew bg-danger"
                                       style={{
                                         fontSize: "0.6875rem",
                                         whiteSpace: "nowrap",
                                         padding: "0.35rem 0.65rem",
                                       }}
                                     >
                                       Deleted
                                     </span>
                                   )}
                                 </div>
                               </div>
     
                               {/* Product Details Grid */}
                               <div className="row g-3 mb-4">
                                 {/* Product Image */}
     
                                 {/* Always show image column */}
                                 <div className="col-lg-3 col-md-4">
                                   <div
                                     style={{
                                       padding: "1rem",
                                       backgroundColor: "#f8f9fa",
                                       border: "1px solid #e9ecef",
                                       borderRadius: "0.25rem",
                                       textAlign: "center",
                                       minHeight: "280px",
                                       display: "flex",
                                       alignItems: "center",
                                       justifyContent: "center",
                                       height: "100%",
                                     }}
                                   >
                                     {item.ImageUrl ? (
                                       <img
                                         src={item.ImageUrl}
                                         alt={item.Name}
                                         style={{
                                           maxWidth: "100%",
                                           maxHeight: "250px",
                                           objectFit: "contain",
                                         }}
                                         onError={(e) => {
                                           e.target.style.display = "none";
                                           e.target.parentElement.innerHTML =
                                             '<i class="bi bi-image" style="font-size: 3rem; color: #adb5bd;"></i>';
                                         }}
                                       />
                                     ) : (
                                       <div
                                         style={{
                                           display: "flex",
                                           flexDirection: "column",
                                           alignItems: "center",
                                           justifyContent: "center",
                                           color: "#adb5bd",
                                         }}
                                       >
                                         <i
                                           className="bi bi-image"
                                           style={{
                                             fontSize: "3rem",
                                             marginBottom: "0.5rem",
                                           }}
                                         ></i>
                                         <span
                                           style={{
                                             fontSize: "0.875rem",
                                             fontStyle: "italic",
                                           }}
                                         >
                                           No Image Available
                                         </span>
                                       </div>
                                     )}
                                   </div>
                                 </div>
     
                                 {/* Product Info */}
                                 <div
                                   className={
                                     item.ImageUrl ? "col-lg-9 col-md-8" : "col-12"
                                   }
                                 >
                                   <div className="row g-3">
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         SKU
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.SKU || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Product ID
                                       </div>
                                       <div
                                         className="font-monospace"
                                         style={{
                                           fontSize: "0.8125rem",
                                           fontWeight: 500,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.ProductId || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Color
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.Color || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Size
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.Size || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Supplier
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.SupplierName || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Subtotal
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.9375rem",
                                           fontWeight: 700,
                                           color: "#212529",
                                         }}
                                       >
                                         ${item.Subtotal?.toFixed(2)}{" "}
                                         {item.CurrencyCode}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Tax
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                         }}
                                       >
                                         ${Number(
                                           item.TaxAmount ??
                                             item.taxAmount ??
                                             item.Tax ??
                                             0
                                         ).toFixed(2)}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Source
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.875rem",
                                           fontWeight: 600,
                                           color: "#212529",
                                           textTransform: "uppercase",
                                         }}
                                       >
                                         {item.Source || "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Created
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.8125rem",
                                           fontWeight: 500,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.createdAt
                                           ? DATE_FMT(item.createdAt)
                                           : "–"}
                                       </div>
                                     </div>
                                     <div className="col-md-3 col-sm-6">
                                       <div
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                           marginBottom: "0.25rem",
                                           fontWeight: 500,
                                         }}
                                       >
                                         Updated
                                       </div>
                                       <div
                                         style={{
                                           fontSize: "0.8125rem",
                                           fontWeight: 500,
                                           color: "#212529",
                                         }}
                                       >
                                         {item.updatedAt
                                           ? DATE_FMT(item.updatedAt)
                                           : "–"}
                                       </div>
                                     </div>
                                   </div>
     
                                   {/* SHORT DESCRIPTION - Always show */}
                                   <div
                                     className="mt-3"
                                     style={{
                                       padding: "0.75rem",
                                       backgroundColor: "#f8f9fa",
                                       border: "1px solid #e9ecef",
                                       borderRadius: "0.25rem",
                                       fontSize: "0.8125rem",
                                       lineHeight: "1.6",
                                     }}
                                   >
                                     <div
                                       style={{
                                         fontSize: "0.75rem",
                                         fontWeight: 600,
                                         color: "#6c757d",
                                         marginBottom: "0.375rem",
                                         textTransform: "uppercase",
                                         letterSpacing: "0.5px",
                                       }}
                                     >
                                       Product Summary - Short Description
                                     </div>
                                     <div
                                       style={{
                                         color: item.ShortDescription?.trim()
                                           ? "#495057"
                                           : "#9ca3af",
                                         fontStyle: item.ShortDescription?.trim()
                                           ? "normal"
                                           : "italic",
                                       }}
                                     >
                                       {item.ShortDescription?.trim() || "–"}
                                     </div>
                                   </div>
     
                                   {/* FULL DESCRIPTION - Always show */}
                                   <div
                                     className="mt-2"
                                     style={{
                                       padding: "0.75rem",
                                       backgroundColor: "#ffffff",
                                       border: "1px solid #dee2e6",
                                       borderRadius: "0.25rem",
                                       fontSize: "0.8125rem",
                                       lineHeight: "1.6",
                                     }}
                                   >
                                     <div
                                       style={{
                                         fontSize: "0.75rem",
                                         fontWeight: 600,
                                         color: "#6c757d",
                                         marginBottom: "0.375rem",
                                         textTransform: "uppercase",
                                         letterSpacing: "0.5px",
                                       }}
                                     >
                                       Full Product Description
                                     </div>
                                     <div
                                       style={{
                                         color: item.Description?.trim()
                                           ? "#495057"
                                           : "#9ca3af",
                                         fontStyle: item.Description?.trim()
                                           ? "normal"
                                           : "italic",
                                       }}
                                     >
                                       {item.Description?.trim() || "–"}
                                     </div>
                                   </div>
     
                                   {/* </div> */}
                                 </div>
                               </div>
     
                               {/* Decorations Section */}
                               {item.decorations && item.decorations.length > 0 && (
                                 <div>
                                   <h6 className="section-header">
                                     <i className="bi bi-palette me-2"></i>
                                     Decoration Details ({decorationCount} Decoration
                                     {decorationCount !== 1 ? "s" : ""},{" "}
                                     {totalCharges} Charge
                                     {totalCharges !== 1 ? "s" : ""})
                                   </h6>
     
                                   {item.decorations.map((deco, decoIdx) => (
                                     <div
                                       key={deco.id}
                                       style={{ marginBottom: "1.5rem" }}
                                     >
                                       {/* Location Header */}
                                       <div
                                         className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3"
                                         style={{
                                           padding: "0.75rem",
                                           backgroundColor: "#f8f9fa",
                                           border: "1px solid #e9ecef",
                                           borderRadius: "0.25rem",
                                         }}
                                       >
                                         <div className="d-flex align-items-center gap-3 flex-wrap">
                                           <div className="d-flex align-items-center">
                                             <i
                                               className="bi bi-pin-map text-primary me-2"
                                               style={{ fontSize: "1rem" }}
                                             ></i>
                                             <span
                                               style={{
                                                 fontSize: "0.875rem",
                                                 fontWeight: 700,
                                                 color: "#212529",
                                               }}
                                             >
                                               Location: {deco.Location || "–"}
                                             </span>
                                           </div>
                                           <span
                                             className="badge badgeNew bg-secondary text-dark font-monospace"
                                             style={{ fontSize: "0.65rem" }}
                                           >
                                             Decoration ID: {deco.id}
                                           </span>
                                           <span
                                             className="badge badgeNew bg-secondary text-dark font-monospace"
                                             style={{ fontSize: "0.65rem" }}
                                           >
                                             Item ID: {deco.OrderItemId}
                                           </span>
                                           {deco.DecoratorDetails
                                             ?.isEliteDecorator && (
                                             <span
                                               className="badge badgeNew bg-success"
                                               style={{ fontSize: "0.65rem" }}
                                             >
                                               <i className="bi bi-star-fill me-1"></i>
                                               Elite Decorator
                                             </span>
                                           )}
                                           {deco.DecoratorDetails?.source && (
                                             <span
                                               className="badge badgeNew bg-info"
                                               style={{ fontSize: "0.65rem" }}
                                             >
                                               Source: {deco.DecoratorDetails.source}
                                             </span>
                                           )}
                                         </div>
                                         {deco.Charge && deco.Charge.length > 0 && (
                                           <span
                                             className="badge badgeNew bg-primary"
                                             style={{ fontSize: "0.7rem" }}
                                           >
                                             {deco.Charge.length} Charge
                                             {deco.Charge.length !== 1 ? "s" : ""}
                                           </span>
                                         )}
                                       </div>
     
                                       {/* Decorator Details */}
                                       {deco.DecoratorDetails && (
                                         <div
                                           className="alert alert-light border mb-3"
                                           style={{
                                             fontSize: "0.8125rem",
                                             padding: "0.75rem",
                                           }}
                                         >
                                           <div className="row g-2">
                                             <div className="col-md-4">
                                               <strong>Decorator ID:</strong>{" "}
                                               {deco.DecoratorDetails.decoratorId ||
                                                 "–"}
                                             </div>
                                             <div className="col-md-4">
                                               <strong>Details ID:</strong>{" "}
                                               {deco.DecoratorDetails.id || "–"}
                                             </div>
                                             <div className="col-md-4">
                                               <strong>Charge ID:</strong>{" "}
                                               {deco.DecoratorDetails.chargeId ||
                                                 "–"}
                                             </div>
                                             {deco.DecoratorDetails.methodName && (
                                               <div className="col-12">
                                                 <strong>Method:</strong>{" "}
                                                 {deco.DecoratorDetails.methodName}
                                               </div>
                                             )}
                                             {deco.DecoratorDetails.rowBindings &&
                                               deco.DecoratorDetails.rowBindings
                                                 .length > 0 && (
                                                 <div className="col-12">
                                                   <strong>Row Bindings:</strong>{" "}
                                                   {
                                                     deco.DecoratorDetails
                                                       .rowBindings.length
                                                   }{" "}
                                                   binding
                                                   {deco.DecoratorDetails.rowBindings
                                                     .length !== 1
                                                     ? "s"
                                                     : ""}
                                                 </div>
                                               )}
                                           </div>
                                         </div>
                                       )}
     
                                       <h6 className="section-header">
                                         <i className="bi bi-tags me-2"></i>Charges
                                         by Department
                                       </h6>
     
                                       {/* Charges Table - COMPLETE WITH ALL FIELDS */}
                                       {deco.Charge && deco.Charge.length > 0 && (
                                         <div className="table-responsive mb-3">
                                           <table className="table overview-table table-bordered mb-0">
                                             <thead>
                                               <tr>
                                                 <th>Charge Name</th>
                                                 <th className="text-center">
                                                   Type
                                                 </th>
                                                 <th className="text-center">Qty</th>
                                                 <th className="text-end">
                                                   Net Cost
                                                 </th>
                                                 <th className="text-end">Retail</th>
                                                 <th className="text-end">
                                                   Client Price
                                                 </th>
                                                 <th className="text-center">
                                                   Margin %
                                                 </th>
                                                 <th className="text-end">Total</th>
                                                 <th className="text-center">
                                                   Display
                                                 </th>
                                                 {/* <th className="text-center">IDs</th> */}
                                               </tr>
                                             </thead>
                                             <tbody>
                                               {deco.Charge.map(
                                                 (charge, chargeIdx) => (
                                                   <tr key={chargeIdx}>
                                                     <td style={{ fontWeight: 600 }}>
                                                       {charge.Name || "–"}
                                                     </td>
                                                     <td className="text-center">
                                                       <span
                                                         className="badge badgeNew bg-secondary text-dark"
                                                         style={{
                                                           fontSize: "0.7rem",
                                                         }}
                                                       >
                                                         {charge.Type || "–"}
                                                       </span>
                                                     </td>
                                                     <td
                                                       className="text-center"
                                                       style={{ fontWeight: 600 }}
                                                     >
                                                       {charge.Quantity}
                                                     </td>
                                                     {/* <td className="text-end">
                                                       $
                                                       {(
                                                         charge?.NetCost || 0
                                                       ).toFixed(2)}
                                                     </td> */}
                                                     <td className="text-end">
                                                       $
                                                       {Number(
                                                         charge?.NetCost || 0
                                                       ).toFixed(2)}
                                                     </td>
                                                     {/* <td className="text-end">
                                                       $
                                                       {(charge.Retail || 0).toFixed(
                                                         2
                                                       )}
                                                     </td> */}
                                                     <td className="text-end">
                                                       $
                                                       {Number(
                                                         charge?.Retail || 0
                                                       ).toFixed(2)}
                                                     </td>
                                                     <td
                                                       className="text-end"
                                                       style={{ fontWeight: 600 }}
                                                     >
                                                       $
                                                       {/* {(
                                                         charge.ClientPrice || 0
                                                       ).toFixed(2)} */}
                                                       {(
                                                         parseFloat(
                                                           charge.ClientPrice
                                                         ) || 0
                                                       ).toFixed(2)}
                                                     </td>
                                                     <td className="text-center">
                                                       <span
                                                         className="badge"
                                                         style={{
                                                           backgroundColor:
                                                             charge.Margin >= 40
                                                               ? "#d1fae5"
                                                               : "#fef3c7",
                                                           color:
                                                             charge.Margin >= 40
                                                               ? "#065f46"
                                                               : "#92400e",
                                                           fontSize: "0.7rem",
                                                         }}
                                                       >
                                                         {charge.Margin}%
                                                       </span>
                                                     </td>
                                                     <td
                                                       className="text-end"
                                                       style={{
                                                         fontWeight: 700,
                                                         fontSize: "0.9375rem",
                                                       }}
                                                     >
                                                       $
                                                       {/* {(charge.Total || 0).toFixed(
                                                         2
                                                       )} */}
                                                       {Number(
                                                         charge.Total || 0
                                                       ).toFixed(2)}
                                                     </td>
                                                     <td className="text-center">
                                                       <span
                                                         className={`badge ${
                                                           charge.DisplayStatus?.includes(
                                                             "Display"
                                                           )
                                                             ? "bg-success"
                                                             : "bg-secondary"
                                                         }`}
                                                         style={{
                                                           fontSize: "0.65rem",
                                                         }}
                                                       >
                                                         {charge.DisplayStatus ||
                                                           "–"}
                                                       </span>
                                                     </td>
                                                     {/* <td
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       <div className="d-flex flex-column gap-1">
                                                         <span className="badge badgeNew bg-secondary font-monospace">
                                                           C:{" "}
                                                           {charge.chargeId || "–"}
                                                         </span>
                                                         <span className="badge badgeNew bg-info font-monospace">
                                                           D:{" "}
                                                           {charge.decoratorId ||
                                                             "–"}
                                                         </span>
                                                         {charge.supplierId && (
                                                           <span className="badge badgeNew bg-warning text-dark font-monospace">
                                                             S: {charge.supplierId}
                                                           </span>
                                                         )}
                                                         {charge.addressId && (
                                                           <span className="badge badgeNew bg-success font-monospace">
                                                             A: {charge.addressId}
                                                           </span>
                                                         )}
                                                       </div>
                                                     </td> */}
                                                   </tr>
                                                 )
                                               )}
                                             </tbody>
                                           </table>
                                         </div>
                                       )}
     
                                       {/* ROW BINDINGS TABLE - NEW SECTION */}
                                       {deco.DecoratorDetails?.rowBindings &&
                                         deco.DecoratorDetails.rowBindings.length >
                                           0 && (
                                           <div className="mb-3">
                                             <div
                                               style={{
                                                 fontSize: "0.75rem",
                                                 fontWeight: 600,
                                                 color: "#495057",
                                                 marginBottom: "0.75rem",
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.5px",
                                                 display: "flex",
                                                 alignItems: "center",
                                                 gap: "0.5rem",
                                               }}
                                             >
                                               <i className="bi bi-diagram-2 text-primary"></i>
                                               <span>
                                                 Decorations (
                                                 {
                                                   deco.DecoratorDetails.rowBindings
                                                     .length
                                                 }
                                                 )
                                               </span>
                                             </div>
                                             <div className="table-responsive">
                                               <table className="table overview-table table-bordered table-sm mb-0">
                                                 <thead>
                                                   <tr
                                                     style={{
                                                       backgroundColor: "#f8f9fa",
                                                     }}
                                                   >
                                                     <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       No.
                                                     </th>
                                                     <th
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Charge Name
                                                     </th>
                                                     {/* <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Charge ID
                                                     </th> */}
                                                     {/* <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Decorator ID
                                                     </th> */}
                                                     <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Department Name
                                                     </th>
     
                                                     <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Department Status
                                                     </th>
                                                     {/* <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Supplier ID
                                                     </th> */}
                                                     {/* <th
                                                       className="text-center"
                                                       style={{ fontSize: "0.7rem" }}
                                                     >
                                                       Address ID
                                                     </th> */}
                                                   </tr>
                                                 </thead>
                                                 <tbody>
                                                   {deco.DecoratorDetails.rowBindings.map(
                                                     (binding, bindingIdx) => (
                                                       <tr key={bindingIdx}>
                                                         <td
                                                           className="text-center"
                                                           style={{
                                                             fontWeight: 700,
                                                             fontSize: "0.8125rem",
                                                             backgroundColor:
                                                               "#f8f9fa",
                                                           }}
                                                         >
                                                           {/* {binding.rowIndex} */}
                                                           {bindingIdx + 1}
                                                         </td>
                                                         <td
                                                           style={{
                                                             fontWeight: 600,
                                                             fontSize: "0.8125rem",
                                                           }}
                                                         >
                                                           {binding.chargeName}
                                                         </td>
                                                         {/* <td className="text-center">
                             <span className="badge badgeNew bg-primary font-monospace" style={{ fontSize: "0.65rem" }}>
                               {binding.chargeId}
                             </span>
                           </td> */}
                                                         {/* <td
                                                           className="text-center font-monospace"
                                                           style={{
                                                             fontSize: "0.8125rem",
                                                             fontWeight: 600,
                                                           }}
                                                         >
                                                           {binding.chargeId}
                                                         </td> */}
                                                         {/* <td className="text-center">
                                                           {binding.decoratorId ? (
                                                             <span
                                                               className="badge badgeNew bg-info font-monospace"
                                                               style={{
                                                                 fontSize: "0.65rem",
                                                               }}
                                                             >
                                                               {binding.decoratorId}
                                                             </span>
                                                           ) : (
                                                             <span className="text-muted small">
                                                               –
                                                             </span>
                                                           )}
                                                         </td> */}
     
                                                         <td
                                                           style={{
                                                             fontWeight: 600,
                                                             fontSize: "0.8125rem",
                                                           }}
                                                         >
                                                           {
                                                             binding.productionDepartmentName
                                                           }
                                                         </td>
                                                         {/* <td className="text-center">
                                                           {binding.productionDepartmentName ? (
                                                             <span
                                                               className="badge badgeNew bg-success font-monospace"
                                                               style={{
                                                                 fontSize: "0.65rem",
                                                               }}
                                                             >
                                                               {
                                                                 binding.productionDepartmentName
                                                               }
                                                             </span>
                                                           ) : (
                                                             <span className="text-muted small">
                                                               –
                                                             </span>
                                                           )}
                                                         </td> */}
                                                         <td className="text-center">
                                                           {binding.status ? (
                                                             <span
                                                               className="badge badgeNew bg-success font-monospace"
                                                               style={{
                                                                 fontSize: "0.65rem",
                                                               }}
                                                             >
                                                               {binding.status}
                                                             </span>
                                                           ) : (
                                                             <span className="text-muted small">
                                                               –
                                                             </span>
                                                           )}
                                                         </td>
                                                         {/* <td className="text-center">
                                                           {binding.supplierId ? (
                                                             <span
                                                               className="badge badgeNew bg-warning text-dark font-monospace"
                                                               style={{
                                                                 fontSize: "0.65rem",
                                                               }}
                                                             >
                                                               {binding.supplierId}
                                                             </span>
                                                           ) : (
                                                             <span className="text-muted small">
                                                               null
                                                             </span>
                                                           )}
                                                         </td> */}
                                                         {/* <td className="text-center">
                                                           {binding.addressId ? (
                                                             <span
                                                               className="badge badgeNew bg-secondary font-monospace"
                                                               style={{
                                                                 fontSize: "0.65rem",
                                                               }}
                                                             >
                                                               {binding.addressId}
                                                             </span>
                                                           ) : (
                                                             <span className="text-muted small">
                                                               null
                                                             </span>
                                                           )}
                                                         </td> */}
                                                       </tr>
                                                     )
                                                   )}
                                                 </tbody>
                                               </table>
                                             </div>
                                           </div>
                                         )}
     
                                       {/* Artwork Details - COMPLETE WITH ALL FIELDS */}
     
                                       {/* Artwork Details - IMPROVED LAYOUT */}
                                       {deco.artworks &&
                                         deco.artworks.length > 0 && (
                                           <div>
                                             <div
                                               style={{
                                                 fontSize: "0.75rem",
                                                 fontWeight: 600,
                                                 color: "#6c757d",
                                                 marginBottom: "0.75rem",
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.5px",
                                               }}
                                             >
                                               <i className="bi bi-image me-1"></i>
                                               Artwork Details (
                                               {deco.artworks.length})
                                             </div>
                                             <div className="row g-3">
                                               {deco.artworks.map((art) => (
                                                 <div
                                                   key={art.id}
                                                   className="col-12"
                                                 >
                                                   <div
                                                     style={{
                                                       padding: "1rem",
                                                       backgroundColor: "#f8f9fa",
                                                       border: "1px solid #e9ecef",
                                                       borderRadius: "0.25rem",
                                                     }}
                                                   >
                                                     {/* Horizontal Layout: Image Left, Details Right */}
                                                     <div className="row g-3">
                                                       {/* Left Side - Artwork Image */}
                                                       {/* {art.Image && (
                                                         <div className="col-lg-4 col-md-5">
                                                           <div
                                                             style={{
                                                               padding: "0.75rem",
                                                               backgroundColor:
                                                                 "#ffffff",
                                                               border:
                                                                 "1px solid #e9ecef",
                                                               borderRadius:
                                                                 "0.25rem",
                                                               textAlign: "center",
                                                               minHeight: "200px",
                                                               display: "flex",
                                                               alignItems: "center",
                                                               justifyContent:
                                                                 "center",
                                                               height: "100%",
                                                             }}
                                                           >
                                                             <img
                                                               src={art.Image}
                                                               alt="Artwork"
                                                               style={{
                                                                 maxWidth: "100%",
                                                                 maxHeight: "180px",
                                                                 objectFit:
                                                                   "contain",
                                                               }}
                                                             />
                                                           </div>
                                                         </div>
                                                       )} */}
     
                                                       {/* Left Side - Artwork Image - Always show */}
                                                       <div className="col-lg-4 col-md-5">
                                                         <div
                                                           style={{
                                                             padding: "0.75rem",
                                                             backgroundColor:
                                                               "#ffffff",
                                                             border:
                                                               "1px solid #e9ecef",
                                                             borderRadius: "0.25rem",
                                                             textAlign: "center",
                                                             minHeight: "200px",
                                                             display: "flex",
                                                             alignItems: "center",
                                                             justifyContent:
                                                               "center",
                                                             height: "100%",
                                                           }}
                                                         >
                                                           {art.Image ? (
                                                             <img
                                                               src={art.Image}
                                                               alt="Artwork"
                                                               style={{
                                                                 maxWidth: "100%",
                                                                 maxHeight: "180px",
                                                                 objectFit:
                                                                   "contain",
                                                               }}
                                                             />
                                                           ) : (
                                                             <div
                                                               style={{
                                                                 display: "flex",
                                                                 flexDirection:
                                                                   "column",
                                                                 alignItems:
                                                                   "center",
                                                                 justifyContent:
                                                                   "center",
                                                                 color: "#9ca3af",
                                                               }}
                                                             >
                                                               <i
                                                                 className="bi bi-image"
                                                                 style={{
                                                                   fontSize: "3rem",
                                                                   marginBottom:
                                                                     "0.5rem",
                                                                 }}
                                                               ></i>
                                                               <span
                                                                 style={{
                                                                   fontSize:
                                                                     "0.875rem",
                                                                   fontStyle:
                                                                     "italic",
                                                                 }}
                                                               >
                                                                 No Image Available
                                                               </span>
                                                             </div>
                                                           )}
                                                         </div>
                                                       </div>
     
                                                       {/* Right Side - Artwork Information */}
                                                       {/* <div
                                                         className={
                                                           art.Image
                                                             ? "col-lg-8 col-md-7"
                                                             : "col-12"
                                                         }
                                                       > */}
     
                                                       <div className="col-lg-8 col-md-7">
                                                         <div
                                                           style={{
                                                             fontSize: "0.8125rem",
                                                           }}
                                                         >
                                                           {/* Two Column Grid for Details */}
                                                           <div className="row g-3">
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Artwork ID:
                                                                 </span>
                                                                 <span className="font-monospace fw-semibold">
                                                                   {art.id}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Type:
                                                                 </span>
                                                            <span className="fw-semibold">
                                                              {art.ImprintType}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                  "#6c757d",
                                                                fontWeight: 500,
                                                              }}
                                                            >
                                                              Location:
                                                            </span>
                                                            <span className="fw-semibold">
                                                              {art.Location ||
                                                                deco.Location ||
                                                                "–"}
                                                            </span>
                                                          </div>
                                                        </div>
                                                        <div className="col-md-6">
                                                          <div className="d-flex justify-content-between py-2 border-bottom">
                                                            <span
                                                              style={{
                                                                color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Logo Name:
                                                                 </span>
                                                                 <span className="fw-semibold" style={{ whiteSpace: "pre-line" }}>
                                                                   {art.LogoName}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Logo Size:
                                                                 </span>
                                                                 <span className="fw-semibold">
                                                                   {art.LogoSize}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Logo Color:
                                                                 </span>
                                                                 <span className="fw-semibold">
                                                                   {art.LogoColor}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Repeat Logo:
                                                                 </span>
                                                                 <span className="fw-semibold">
                                                                   {art.RepeatLogo}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Proof Required:
                                                                 </span>
                                                                 <span
                                                                   className={`badge badgeNew ${
                                                                     art.ProofRequired ===
                                                                     "1"
                                                                       ? "bg-danger"
                                                                       : "bg-success"
                                                                   }`}
                                                                   style={{
                                                                     fontSize:
                                                                       "0.7rem",
                                                                   }}
                                                                 >
                                                                   {art.ProofRequired ===
                                                                   "1"
                                                                     ? `Yes (Action: ${art.ProofRequiredAction})`
                                                                     : "No"}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                             <div className="col-md-6">
                                                               <div className="d-flex justify-content-between py-2 border-bottom">
                                                                 <span
                                                                   style={{
                                                                     color:
                                                                       "#6c757d",
                                                                     fontWeight: 500,
                                                                   }}
                                                                 >
                                                                   Charge:
                                                                 </span>
                                                                 <span className="fw-semibold">
                                                                   $
                                                                   {(
                                                                     art.Charge || 0
                                                                   ).toFixed(2)}
                                                                 </span>
                                                               </div>
                                                             </div>
                                                           </div>
     
                                                           {/* Supplier Notes - Full Width if exists */}
                                                           {/* {art.SupplierNotes && (
                                                             <div className="mt-3 pt-3 border-top">
                                                               <div
                                                                 style={{
                                                                   color: "#6c757d",
                                                                   fontWeight: 600,
                                                                   fontSize:
                                                                     "0.75rem",
                                                                   marginBottom:
                                                                     "0.5rem",
                                                                   textTransform:
                                                                     "uppercase",
                                                                   letterSpacing:
                                                                     "0.5px",
                                                                 }}
                                                               >
                                                                 Supplier Notes
                                                               </div>
                                                               <div
                                                                 style={{
                                                                   color: "#495057",
                                                                   fontSize:
                                                                     "0.8125rem",
                                                                   lineHeight: "1.6",
                                                                 }}
                                                               >
                                                                 {art.SupplierNotes}
                                                               </div>
                                                             </div>
                                                           )} */}
     
                                                           {/* Supplier Notes - Always show section */}
                                                           {/* <div className="mt-3 pt-3 border-top"> */}
                                                           <div className="mt-3">
                                                             <div
                                                               style={{
                                                                 color: "#6c757d",
                                                                 fontWeight: 600,
                                                                 fontSize: "0.75rem",
                                                                 marginBottom:
                                                                   "0.5rem",
                                                                 textTransform:
                                                                   "uppercase",
                                                                 letterSpacing:
                                                                   "0.5px",
                                                               }}
                                                             >
                                                               Supplier Notes
                                                             </div>
                                                             <div
                                                               style={{
                                                                 color:
                                                                   art.SupplierNotes &&
                                                                   art.SupplierNotes.trim()
                                                                     ? "#495057"
                                                                     : "#9ca3af",
                                                                 fontSize:
                                                                   "0.8125rem",
                                                                 lineHeight: "1.6",
                                                                 fontStyle:
                                                                   art.SupplierNotes &&
                                                                   art.SupplierNotes.trim()
                                                                     ? "normal"
                                                                     : "italic",
                                                               }}
                                                             >
                                                               {art.SupplierNotes &&
                                                               art.SupplierNotes.trim()
                                                                 ? art.SupplierNotes
                                                                 : "No supplier notes"}
                                                             </div>
                                                           </div>
     
                                                           {/* Timestamps */}
                                                           <div
                                                             className="d-flex justify-content-between mt-3 pt-3 border-top"
                                                             style={{
                                                               fontSize: "0.75rem",
                                                               color: "#6c757d",
                                                             }}
                                                           >
                                                             <span>
                                                               <i className="bi bi-calendar-plus me-1"></i>
                                                               Art Created:{" "}
                                                               {art.createdAt
                                                                 ? DATE_FMT(
                                                                     art.createdAt
                                                                   )
                                                                 : "–"}
                                                             </span>
                                                             <span>
                                                               <i className="bi bi-calendar-check me-1"></i>
                                                               Art Updated:{" "}
                                                               {art.updatedAt
                                                                 ? DATE_FMT(
                                                                     art.updatedAt
                                                                   )
                                                                 : "–"}
                                                             </span>
                                                           </div>
                                                         </div>
                                                       </div>
                                                     </div>
                                                   </div>
                                                 </div>
                                               ))}
                                             </div>
                                           </div>
                                         )}

                                       {/* Proofing Details - Display-only */}
                                       {(() => {
                                         const proofingItems = [
                                           ...(Array.isArray(deco.proofs)
                                             ? deco.proofs
                                             : []),
                                           ...(Array.isArray(deco.proofings)
                                             ? deco.proofings
                                             : []),
                                           ...(Array.isArray(deco.proofingItems)
                                             ? deco.proofingItems
                                             : []),
                                         ];

                                         if (!proofingItems.length) return null;

                                         const valueOrDash = (v) =>
                                           v === 0 || v ? v : "–";

                                         return (
                                           <div className="mt-3">
                                             <div
                                               style={{
                                                 fontSize: "0.75rem",
                                                 fontWeight: 600,
                                                 color: "#6c757d",
                                                 marginBottom: "0.75rem",
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.5px",
                                               }}
                                             >
                                               <i className="bi bi-check2-square me-1"></i>
                                               Proofing ({proofingItems.length})
                                             </div>
                                             <div className="row g-3">
                                               {proofingItems.map((proof, idx) => {
                                                 const previewSrc =
                                                   proof.Image ||
                                                   proof.image ||
                                                   proof.previewUrl ||
                                                   proof.thumbnailUrl ||
                                                   proof.filePath ||
                                                   proof.proofFilePath ||
                                                   proof.url;
                                                 const fileName =
                                                   proof.fileName ||
                                                   proof.name ||
                                                   (previewSrc &&
                                                     String(previewSrc).split("/").pop());
                                                 const isPdf =
                                                   (fileName &&
                                                     String(fileName)
                                                       .toLowerCase()
                                                       .endsWith(".pdf")) ||
                                                   String(previewSrc || "")
                                                     .toLowerCase()
                                                     .includes(".pdf");

                                                 return (
                                                   <div
                                                     key={proof.id || `${idx}-${fileName || "proof"}`}
                                                     className="col-12"
                                                   >
                                                     <div
                                                       style={{
                                                         padding: "1rem",
                                                         backgroundColor: "#f8f9fa",
                                                         border: "1px solid #e9ecef",
                                                         borderRadius: "0.25rem",
                                                       }}
                                                     >
                                                       <div className="row g-3">
                                                         <div className="col-lg-4 col-md-5">
                                                           <div
                                                             style={{
                                                               padding: "0.75rem",
                                                               backgroundColor: "#ffffff",
                                                               border: "1px solid #e9ecef",
                                                               borderRadius: "0.25rem",
                                                               textAlign: "center",
                                                               minHeight: "200px",
                                                               display: "flex",
                                                               alignItems: "center",
                                                               justifyContent: "center",
                                                               height: "100%",
                                                             }}
                                                           >
                                                             {previewSrc && !isPdf ? (
                                                               <img
                                                                 src={previewSrc}
                                                                 alt={fileName || "Proof"}
                                                                 style={{
                                                                   maxWidth: "100%",
                                                                   maxHeight: "180px",
                                                                   objectFit: "contain",
                                                                 }}
                                                               />
                                                             ) : (
                                                               <div
                                                                 style={{
                                                                   display: "flex",
                                                                   flexDirection: "column",
                                                                   alignItems: "center",
                                                                   justifyContent: "center",
                                                                   color: "#9ca3af",
                                                                 }}
                                                               >
                                                                 <i
                                                                   className={`bi ${
                                                                     previewSrc && isPdf
                                                                       ? "bi-file-earmark-pdf"
                                                                       : "bi-image"
                                                                   }`}
                                                                   style={{
                                                                     fontSize: "3rem",
                                                                     marginBottom: "0.5rem",
                                                                   }}
                                                                 ></i>
                                                                 <span
                                                                   style={{
                                                                     fontSize: "0.875rem",
                                                                     fontStyle: "italic",
                                                                   }}
                                                                 >
                                                                   {previewSrc && isPdf
                                                                     ? "PDF Preview"
                                                                     : "No Preview Available"}
                                                                 </span>
                                                               </div>
                                                             )}
                                                           </div>
                                                         </div>

                                                         <div className="col-lg-8 col-md-7">
                                                           <div style={{ fontSize: "0.8125rem" }}>
                                                             <div className="row g-3">
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>File Name:</span>
                                                                   <span className="fw-semibold text-end ms-2">{valueOrDash(fileName)}</span>
                                                                 </div>
                                                               </div>
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>Proof Status:</span>
                                                                   <span className="fw-semibold">{valueOrDash(proof.status || proof.proofStatus)}</span>
                                                                 </div>
                                                               </div>
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>Uploaded By:</span>
                                                                   <span className="fw-semibold">{valueOrDash(proof.uploadedByName || proof.uploadedBy || proof.createdByName)}</span>
                                                                 </div>
                                                               </div>
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>Approval State:</span>
                                                                   <span className="fw-semibold">{valueOrDash(proof.approvalState || proof.approvalStatus || proof.clientApprovalStatus)}</span>
                                                                 </div>
                                                               </div>
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>Location:</span>
                                                                   <span className="fw-semibold">{valueOrDash(proof.location || proof.Location || deco.Location)}</span>
                                                                 </div>
                                                               </div>
                                                               <div className="col-md-6">
                                                                 <div className="d-flex justify-content-between py-2 border-bottom">
                                                                   <span style={{ color: "#6c757d", fontWeight: 500 }}>Decoration:</span>
                                                                   <span className="fw-semibold">{valueOrDash(proof.decorationName || deco.DecorationMethod || deco.ImprintType)}</span>
                                                                 </div>
                                                               </div>
                                                             </div>

                                                             <div className="mt-3">
                                                               <div
                                                                 style={{
                                                                   color: "#6c757d",
                                                                   fontWeight: 600,
                                                                   fontSize: "0.75rem",
                                                                   marginBottom: "0.5rem",
                                                                   textTransform: "uppercase",
                                                                   letterSpacing: "0.5px",
                                                                 }}
                                                               >
                                                                 Notes
                                                               </div>
                                                               <div
                                                                 style={{
                                                                   color: proof.notes || proof.note ? "#495057" : "#9ca3af",
                                                                   fontSize: "0.8125rem",
                                                                   lineHeight: "1.6",
                                                                   fontStyle: proof.notes || proof.note ? "normal" : "italic",
                                                                 }}
                                                               >
                                                                 {proof.notes || proof.note || "No proof notes"}
                                                               </div>
                                                             </div>

                                                             <div
                                                               className="d-flex justify-content-between mt-3 pt-3 border-top"
                                                               style={{ fontSize: "0.75rem", color: "#6c757d" }}
                                                             >
                                                               <span>
                                                                 <i className="bi bi-calendar-plus me-1"></i>
                                                                 Created: {proof.createdAt ? DATE_FMT(proof.createdAt) : "–"}
                                                               </span>
                                                               <span>
                                                                 <i className="bi bi-calendar-check me-1"></i>
                                                                 Updated: {proof.updatedAt ? DATE_FMT(proof.updatedAt) : "–"}
                                                               </span>
                                                             </div>
                                                           </div>
                                                         </div>
                                                       </div>
                                                     </div>
                                                   </div>
                                                 );
                                               })}
                                             </div>
                                           </div>
                                         );
                                       })()}

                                       {/* Production-only files for the production team.
                                           The order-details endpoint already scopes these
                                           files to the current item/decoration. Render once
                                           in the overview immediately after Proofing. */}
                                       {decoIdx === 0 && (() => {
                                         const productionFiles = Array.isArray(orderMeta?.productionFiles)
                                           ? orderMeta.productionFiles
                                           : [];

                                         return (
                                           <div className="mt-3">
                                             <div
                                               style={{
                                                 fontSize: "0.75rem",
                                                 fontWeight: 600,
                                                 color: "#6c757d",
                                                 marginBottom: "0.75rem",
                                                 textTransform: "uppercase",
                                                 letterSpacing: "0.5px",
                                               }}
                                             >
                                               <i className="bi bi-file-earmark-arrow-down me-1"></i>
                                               Production Files ({productionFiles.length})
                                             </div>

                                             <div
                                               style={{
                                                 padding: "1rem",
                                                 backgroundColor: "#f8f9fa",
                                                 border: "1px solid #e9ecef",
                                                 borderRadius: "0.25rem",
                                               }}
                                             >
                                               <div
                                                 style={{
                                                   fontSize: "0.75rem",
                                                   color: "#6c757d",
                                                   marginBottom: productionFiles.length ? "0.75rem" : 0,
                                                 }}
                                               >
                                                 Internal source and production-ready files. These do not require customer approval.
                                               </div>

                                               {productionFiles.length > 0 ? (
                                                 <div className="d-flex flex-column gap-2">
                                                   {productionFiles.map((file, fileIndex) => {
                                                     const fileUrl =
                                                       file?.filePath ||
                                                       file?.sourceFilePath ||
                                                       file?.downloadUrl ||
                                                       file?.url ||
                                                       "";
                                                     const fileName =
                                                       file?.fileName ||
                                                       file?.sourceFileName ||
                                                       (fileUrl
                                                         ? String(fileUrl).split("/").pop()
                                                         : `Production file ${fileIndex + 1}`);
                                                     const sourceLabel =
                                                       file?.sourceProvider ||
                                                       file?.sourceType ||
                                                       file?.source ||
                                                       "Production";

                                                     if (!fileUrl) return null;

                                                     return (
                                                       <div
                                                         key={
                                                           file?.sourceFileId ||
                                                           file?.fileId ||
                                                           file?.id ||
                                                           fileUrl ||
                                                           fileIndex
                                                         }
                                                         className="d-flex align-items-center justify-content-between gap-3 flex-wrap"
                                                         style={{
                                                           padding: "0.75rem",
                                                           backgroundColor: "#ffffff",
                                                           border: "1px solid #dee2e6",
                                                           borderRadius: "0.25rem",
                                                         }}
                                                       >
                                                         <div
                                                           className="d-flex align-items-center gap-2"
                                                           style={{ minWidth: 0, flex: "1 1 300px" }}
                                                         >
                                                           <i
                                                             className="bi bi-file-earmark-code text-primary"
                                                             style={{ fontSize: "1.15rem" }}
                                                           ></i>
                                                           <div style={{ minWidth: 0 }}>
                                                             <div
                                                               className="fw-semibold text-truncate"
                                                               title={fileName}
                                                               style={{ fontSize: "0.8125rem" }}
                                                             >
                                                               {fileName}
                                                             </div>
                                                             <div
                                                               className="text-muted"
                                                               style={{ fontSize: "0.7rem" }}
                                                             >
                                                               {sourceLabel}
                                                             </div>
                                                           </div>
                                                         </div>

                                                         <a
                                                           href={fileUrl}
                                                           download={fileName}
                                                           target="_blank"
                                                           rel="noopener noreferrer"
                                                           className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1"
                                                           title={`Download ${fileName}`}
                                                         >
                                                           <i className="bi bi-download"></i>
                                                           Download
                                                         </a>
                                                       </div>
                                                     );
                                                   })}
                                                 </div>
                                               ) : (
                                                 <div
                                                   className="text-muted"
                                                   style={{ fontSize: "0.8125rem", fontStyle: "italic" }}
                                                 >
                                                   No production files are attached yet.
                                                 </div>
                                               )}
                                             </div>
                                           </div>
                                         );
                                       })()}
     
                                       {/* Decoration Timestamps */}
                                       <div
                                         className="d-flex justify-content-between mt-3 pt-2 border-top"
                                         style={{
                                           fontSize: "0.75rem",
                                           color: "#6c757d",
                                         }}
                                       >
                                         <span>
                                           Created:{" "}
                                           {deco.createdAt
                                             ? DATE_FMT(deco.createdAt)
                                             : "–"}
                                         </span>
                                         <span>
                                           Updated:{" "}
                                           {deco.updatedAt
                                             ? DATE_FMT(deco.updatedAt)
                                             : "–"}
                                         </span>
                                       </div>
                                     </div>
                                   ))}
                                 </div>
                               )}
                             </div>
                           </div>
                         );
                       })}
     
                       {/* Batches Section */}
                       {hasSibs && (
                         <div
                           className="card overview-card border-0"
                           style={{ backgroundColor: "#ffffff" }}
                         >
                           <div className="card-body" style={{ padding: "1.25rem" }}>
                             <div className="d-flex justify-content-between align-items-center mb-3">
                               <h6 className="section-header mb-0 pb-0 border-0">
                                 <i className="bi bi-layers me-2"></i>Batches
                               </h6>
                               <span
                                 className="badge badgeNew bg-primary"
                                 style={{ fontSize: "0.6875rem" }}
                               >
                                 {siblingsAll.length} Total
                               </span>
                             </div>
                             <div className="table-responsive">
                               <table className="table overview-table table-bordered mb-0">
                                 <thead>
                                   <tr>
                                     <th>Batch ID</th>
                                     <th>Status</th>
                                     <th className="text-center">Quantity</th>
                                     <th>Machine</th>
                                     <th>Planned Date</th>
                                   </tr>
                                 </thead>
                                 <tbody>
                                   {siblingsAll.map((s) => {
                                     const isCurrent = s.step_id === card.step_id;
                                     return (
                                       <tr
                                         key={s.id}
                                         style={{
                                           backgroundColor: isCurrent
                                             ? "#f8f9fa"
                                             : "#ffffff",
                                         }}
                                       >
                                         <td
                                           style={{
                                             fontWeight: isCurrent ? 700 : 500,
                                           }}
                                         >
                                           {isCurrent && (
                                             <i className="bi bi-arrow-right-circle-fill text-primary me-2"></i>
                                           )}
                                           {isCurrent
                                             ? "Current Batch"
                                             : s.batchLabel ||
                                               s.displayNumber ||
                                               s.id}
                                         </td>
                                         <td>
                                           {s.status?.replace(/_/g, " ") || "–"}
                                         </td>
                                         <td
                                           className="text-center"
                                           style={{ fontWeight: 600 }}
                                         >
                                           {s.qtyPlanned ?? 0}
                                         </td>
                                         <td>{s.machine || "–"}</td>
                                         <td>
                                           {s.plannedStart
                                             ? DATE_FMT(s.plannedStart)
                                             : "–"}
                                         </td>
                                       </tr>
                                     );
                                   })}
                                 </tbody>
                               </table>
                             </div>
                           </div>
                         </div>
                       )}
                     </>
                   )}
     
                   {/* No Details State */}
                   {!modalLoading && !detailedCard && (
                     <div className="text-center" style={{ padding: "5rem 1.5rem" }}>
                       <i
                         className="bi bi-inbox"
                         style={{ fontSize: "4rem", color: "#dee2e6" }}
                       ></i>
                       <div
                         className="mt-3"
                         style={{
                           fontSize: "0.9375rem",
                           fontWeight: 600,
                           color: "#6c757d",
                         }}
                       >
                         No order details available
                       </div>
                       <div style={{ fontSize: "0.8125rem", color: "#adb5bd" }}>
                         Select an order to view details
                       </div>
                     </div>
                   )}
                 </div>
               </>
             )}
     
             {/* {tab === "overview2" && (
               <div
                 style={{
                   padding: 16,
                   display: "grid",
                   gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))",
                   gap: 16,
                 }}
               >
                 {modalLoading && (
                   <div
                     style={{ gridColumn: "1/-1", textAlign: "center", padding: 20 }}
                   >
                     Loading…
                   </div>
                 )}
     
                 {!modalLoading && detailedCard && (
                   <>
                     <Section title="Project Information" style={sectionCard}>
                       <Row l="Project name" v={detailedCard.ProjectName || "–"} />
                       <Row l="Project Number" v={detailedCard.OrderId || "–"} />
                       <RowChip
                         l="PO Numbers"
                         list={
                           Array.isArray(detailedCard.poNumbers)
                             ? detailedCard.poNumbers
                             : []
                         }
                       />
                     </Section>
     
                     <Section title="Product Summary" style={sectionCard}>
                       <Row
                         l="Sizes"
                         v={(aggregatedSizes || []).join(", ") || "–"}
                       />
                       <Row
                         l="Colors"
                         v={(aggregatedColors || []).join(", ") || "–"}
                       />
                       <Row l="Net cost goods" v={money(totalNetCostGoods ?? 0)} />
                       <Row
                         l="Retail cost goods"
                         v={money(totalRetailCostGoods ?? 0)}
                       />
                       <Row l="Supplier information" v={supplierName} />
                       <Row l="Category" v={category} />
                     </Section>
     
                     {detailedCard.Items?.map((item) => (
                       <Section
                         key={item.id}
                         title={`Product: ${item.Name || "–"}`}
                         style={sectionCard}
                       >
                         <Row l="SKU" v={item.ProductId || "–"} />
                         <Row l="Name" v={item.Name || "–"} />
                         <Row l="Quantity" v={item.Quantity ?? 0} />
                         <Row l="Color" v={item.Color || "–"} />
                         <Row l="Size" v={item.Size || "–"} />
                         <Row
                           l="Description"
                           v={item.ShortDescription || item.Description || "–"}
                         />
                         <Row
                           l="Image"
                           v={
                             item.ImageUrl ? (
                               <img
                                 src={item.ImageUrl}
                                 alt={item.Name}
                                 style={{ maxWidth: 100 }}
                               />
                             ) : (
                               "–"
                             )
                           }
                         />
     
                         {item.decorations?.map((dec) => (
                           <div
                             key={dec.id}
                             style={{ marginTop: 8, paddingLeft: 8 }}
                           >
                             <Row
                               l="Deco Dept"
                               v={dec.DecoratorDetails?.source || "–"}
                             />
                             {dec.Charge?.map((ch, i) => (
                               <Row
                                 key={i}
                                 l={ch.Name || "–"}
                                 v={`Total: ${ch.Total ?? 0}, Net: ${
                                   ch.NetCost ?? 0
                                 }, Retail: ${ch.Retail ?? 0}`}
                               />
                             ))}
                           </div>
                         ))}
                       </Section>
                     ))}
     
                     <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                       <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                         <svg
                           className="w-5 h-5 mr-2 text-orange-600"
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path
                             strokeLinecap="round"
                             strokeLinejoin="round"
                             strokeWidth={2}
                             d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                           />
                         </svg>
                         Decoration Information
                       </h3>
                       <div className="space-y-3">
                         <div className="flex justify-between">
                           <span className="text-gray-600 text-sm">
                             Deco department
                           </span>
                           <span className="font-medium text-gray-900">
                             {card.claimedDept ||
                               card.department ||
                               detailedCard?.Items?.[0]?.decorations?.[0]
                                 ?.DecoratorDetails?.source ||
                               "–"}
                           </span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600 text-sm">
                             Matrix pricing
                           </span>
                           <span className="font-medium text-gray-900">
                             {card.matrix ||
                               card.chargeName ||
                               detailedCard?.Items?.[0]?.decorations?.[0]
                                 ?.Charge?.[0]?.Name ||
                               "–"}
                           </span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600 text-sm">
                             Net cost goods
                           </span>
                           <span className="font-medium text-gray-900">
                             {money(
                               detailedCard?.Items?.reduce((sum, item) => {
                                 return (
                                   sum +
                                   (item.decorations?.reduce((decoSum, deco) => {
                                     return (
                                       decoSum +
                                       (deco.Charge?.reduce((chargeSum, charge) => {
                                         return (
                                           chargeSum +
                                           parseFloat(charge.NetCost || 0)
                                         );
                                       }, 0) || 0)
                                     );
                                   }, 0) || 0)
                                 );
                               }, 0) ||
                                 card.netCostGoods ||
                                 0
                             )}
                           </span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600 text-sm">
                             Retail cost goods
                           </span>
                           <span className="font-medium text-gray-900">
                             {money(
                               detailedCard?.Items?.reduce((sum, item) => {
                                 return (
                                   sum +
                                   (item.decorations?.reduce((decoSum, deco) => {
                                     return (
                                       decoSum +
                                       (deco.Charge?.reduce((chargeSum, charge) => {
                                         return (
                                           chargeSum + parseFloat(charge.Retail || 0)
                                         );
                                       }, 0) || 0)
                                     );
                                   }, 0) || 0)
                                 );
                               }, 0) ||
                                 card.retailCostGoods ||
                                 0
                             )}
                           </span>
                         </div>
                         <div className="flex justify-between">
                           <span className="text-gray-600 text-sm">Location</span>
                           <span className="font-medium text-gray-900">
                             {detailedCard?.Items?.[0]?.decorations?.[0]?.Location ||
                               card.location ||
                               "–"}
                           </span>
                         </div>
                       </div>
                     </div>
     
                     <Section title="Art Information" style={sectionCard}>
                       {detailedCard.artProofUrl ? (
                         <a
                           href={detailedCard.artProofUrl}
                           target="_blank"
                           rel="noreferrer"
                           style={{ textDecoration: "underline" }}
                         >
                           Open proof
                         </a>
                       ) : (
                         <div style={ui.value}>–</div>
                       )}
                     </Section>
     
                     <Section title="Customer & Dates" style={sectionCard}>
                       <Row l="Customer" v={detailedCard.CustomerName || "–"} />
                       <Row
                         l="Qty (this / total)"
                         v={detailedCard.qtyPlanned ?? detailedCard.rootQty ?? 0}
                       />
                       <Row l="Decorator PO In-Hands" v={formatProductionBusinessDate(detailedCard.decoratorPoInHandsDate)} />
                       <Row
                         l="Customer In-Hands"
                         v={
                           detailedCard.inHandsDate
                             ? formatProductionBusinessDate(detailedCard.inHandsDate)
                             : "–"
                         }
                       />
                       <Row
                         l="Scheduled Date"
                         v={
                           detailedCard.plannedStart
                             ? DATE_FMT(detailedCard.plannedStart)
                             : "–"
                         }
                       />
                       <Row
                         l="Machine"
                         v={detailedCard.machine || (hasSibs ? "Multi" : "–")}
                       />
                       <Row
                         l="Order Status (SO)"
                         v={detailedCard.OrderStatus?.SO || "–"}
                       />
                       <Row
                         l="Order Status (PO)"
                         v={detailedCard.OrderStatus?.PO || "–"}
                       />
                     </Section>
     
                     {hasSibs && (
                       <div style={{ gridColumn: "1/-1" }}>
                         <div
                           style={{ ...ui.subtle, marginBottom: 6, fontWeight: 600 }}
                         >
                           Batches
                         </div>
                         <div
                           style={{
                             border: "1px solid #eee",
                             borderRadius: 8,
                             overflow: "hidden",
                           }}
                         >
                           <table
                             style={{
                               width: "100%",
                               borderCollapse: "collapse",
                               fontSize: 12,
                             }}
                           >
                             <thead>
                               <tr style={{ background: "#f9fafb" }}>
                                 <th style={th}>ID</th>
                                 <th style={th}>Lane</th>
                                 <th style={th}>Qty</th>
                                 <th style={th}>Machine</th>
                                 <th style={th}>Date</th>
                               </tr>
                             </thead>
                             <tbody>
                               {siblingsAll.map((s) => (
                                 <tr
                                   key={s.id}
                                   style={{ borderTop: "1px solid #eee" }}
                                 >
                                   <td style={td}>
                                     {s.step_id === card.step_id ? (
                                       <b>(This)</b>
                                     ) : (
                                       <span>
                                         {s.batchLabel || s.displayNumber || s.id}
                                       </span>
                                     )}
                                   </td>
     
                                   <td style={td}>
                                     {s.status?.replace(/_/g, " ") || "–"}
                                   </td>
                                   <td style={td}>{s.qtyPlanned ?? 0}</td>
                                   <td style={td}>{s.machine || "–"}</td>
                                   <td style={td}>
                                     {s.plannedStart
                                       ? DATE_FMT(s.plannedStart)
                                       : "–"}
                                   </td>
                                 </tr>
                               ))}
                             </tbody>
                           </table>
                         </div>
                       </div>
                     )}
                   </>
                 )}
     
                 {!modalLoading && !detailedCard && (
                   <div
                     style={{ gridColumn: "1/-1", textAlign: "center", padding: 20 }}
                   >
                     No details available.
                   </div>
                 )}
               </div>
             )} */}
     
             {tab === "decoratorNetwork" && (
               <div style={{ padding: "1.5rem", background: "#f5f6f8", minHeight: 420 }}>
                 <div className="card border-0 shadow-sm mb-3">
                   <div className="card-body">
                     <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
                       <div>
                         <h5 className="mb-1">Decorator Network</h5>
                         <div className="text-muted small">
                           Send this production job to Core Network for decorator matching, offers, assignment, and provider progress updates.
                         </div>
                       </div>
                       <div className="d-flex gap-2 flex-wrap">
                         <button className="btn btn-outline-secondary btn-sm" onClick={loadDecoratorNetwork} disabled={networkLoading || !productionJobId}>
                           {networkLoading ? "Refreshing..." : "Refresh"}
                         </button>
                         <button
                           className="btn btn-primary btn-sm"
                           disabled={networkBusy === "create" || !productionJobId}
                           onClick={() =>
                             runNetworkAction("create", async () => {
                               await createCoreNetworkProductionWorkRequest(productionJobId, buildProductionWorkRequestPayload());
                             })
                           }
                         >
                           {networkBusy === "create" ? "Creating..." : (networkRequest ? "Create New Request" : "Send To Core Network")}
                         </button>
                       </div>
                     </div>
                     <div className="row g-3 mt-1">
                       <div className="col-md-4">
                         <div className="text-muted small">Production Job</div>
                         <div className="fw-semibold">{productionJobId || "Unavailable"}</div>
                       </div>
                       <div className="col-md-4">
                         <div className="text-muted small">Decoration Method</div>
                         <div className="fw-semibold">{card?.processName || currentDecoration?.Location || "Not mapped"}</div>
                       </div>
                       <div className="col-md-4">
                         <div className="text-muted small">Quantity</div>
                         <div className="fw-semibold">{card?.qtyPlanned ?? card?.qtyRequired ?? currentDetailItem?.Quantity ?? 0}</div>
                       </div>
                     </div>
                     {networkError ? <div className="alert alert-danger mt-3 mb-0">{networkError}</div> : null}
                   </div>
                 </div>

                 {networkRequest ? (
                   <>
                     <div className="card border-0 shadow-sm mb-3">
                       <div className="card-body">
                         <div className="row g-3">
                           <div className="col-md-3">
                             <div className="text-muted small">Work Request</div>
                             <div className="fw-semibold">#{networkRequest.id}</div>
                           </div>
                           <div className="col-md-3">
                             <div className="text-muted small">Status</div>
                             <div className="fw-semibold text-capitalize">{String(networkRequest.status || "-").replace(/_/g, " ")}</div>
                           </div>
                           <div className="col-md-3">
                             <div className="text-muted small">Provider Status</div>
                             <div className="fw-semibold text-capitalize">{String(networkRequest.rawStatus || networkRequest.status || "-").replace(/_/g, " ")}</div>
                           </div>
                           <div className="col-md-3">
                             <div className="text-muted small">Deadline</div>
                             <div className="fw-semibold">{networkRequest.deadlineAt ? DATE_FMT(networkRequest.deadlineAt) : "—"}</div>
                           </div>
                         </div>
                       </div>
                     </div>

                     <CoreNetworkMatchPanel
                       matches={networkMatches}
                       selectedMatchIds={selectedMatchIds}
                       onToggleMatch={toggleSelectedMatch}
                       onGenerateMatches={() => runNetworkAction("generate", () => generateCoreNetworkMatches(networkRequest.id, { instanceUniqueId, regenerate: true }))}
                       onShortlistMatches={() => runNetworkAction("shortlist", () => shortlistCoreNetworkMatches(networkRequest.id, { instanceUniqueId, matchIds: selectedMatchIds }))}
                       onSendOffers={() => runNetworkAction("offers", () => sendCoreNetworkOffers(networkRequest.id, { instanceUniqueId, limit: Math.min(Math.max(selectedMatchesCount || 3, 3), 5) }))}
                       busyAction={networkBusy}
                     />

                     <div className="card border-0 shadow-sm mb-3">
                       <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-3">
                         <div>
                           <div className="fw-semibold">Direct Assignment</div>
                           <div className="text-muted small">
                             Assign a selected match immediately without waiting for offer acceptance.
                           </div>
                         </div>
                         <button
                           className="btn btn-outline-primary btn-sm"
                           disabled={networkBusy === "assign" || !selectedPrimaryMatch}
                           onClick={() =>
                             runNetworkAction("assign", () =>
                               assignCoreNetworkWorkRequest(networkRequest.id, {
                                 instanceUniqueId,
                                 memberId: selectedPrimaryMatch?.memberId || selectedPrimaryMatch?.member?.id,
                                 membershipId: selectedPrimaryMatch?.membershipId || selectedPrimaryMatch?.membership?.id,
                               })
                             )
                           }
                         >
                           {networkBusy === "assign" ? "Assigning..." : "Direct Assign Selected Match"}
                         </button>
                       </div>
                     </div>

                     <CoreNetworkOfferPanel
                       offers={networkOffers}
                       busyOfferId={networkBusy === "offer-cancel" ? -1 : null}
                       onAcceptOffer={null}
                       onDeclineOffer={null}
                       onCancelOffer={(offer) =>
                         runNetworkAction("offer-cancel", () => cancelCoreNetworkOffer(offer.id, { instanceUniqueId, reason: "Cancelled from production module" }))
                       }
                     />

                     <CoreNetworkAssignmentPanel assignments={activeAssignments} />

                     <div className="card border-0 shadow-sm mb-3">
                       <div className="card-body">
                         <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                           <div>
                             <div className="fw-semibold">External Provider Status</div>
                             <div className="text-muted small">
                               Latest Core Network production update from the assigned decorator/provider.
                             </div>
                           </div>
                           <div className="d-flex flex-wrap gap-2 align-items-center">
                             {networkRequest?.providerStatusSummary ? (
                               <span className="badge text-bg-info text-uppercase">
                                 {String(networkRequest.providerStatusSummary).replace(/_/g, " ")}
                               </span>
                             ) : (
                               <span className="text-muted small">No provider update yet</span>
                             )}
                             {activeAssignment?.providerStatusUpdatedAt ? (
                               <span className="text-muted small">
                                 Updated {new Date(activeAssignment.providerStatusUpdatedAt).toLocaleString()}
                               </span>
                             ) : null}
                           </div>
                         </div>
                         <div className="row g-3 align-items-end">
                           <div className="col-md-4">
                             <label className="form-label small text-muted">Provider Status</label>
                             <select className="form-select" value={providerStatus} onChange={(event) => setProviderStatus(event.target.value)}>
                               {providerStatusOptions.map((status) => (
                                 <option key={status} value={status}>
                                   {status.replace(/_/g, " ")}
                                 </option>
                               ))}
                             </select>
                           </div>
                           <div className="col-md-5">
                             <label className="form-label small text-muted">Status Note</label>
                             <input className="form-control" value={providerStatusNote} onChange={(event) => setProviderStatusNote(event.target.value)} placeholder="Optional provider update note" />
                           </div>
                           <div className="col-md-3 d-flex gap-2">
                             <button
                               className="btn btn-outline-secondary w-100"
                               disabled={networkBusy === "provider-status"}
                               onClick={() =>
                                 runNetworkAction("provider-status", () =>
                                   updateCoreNetworkProviderStatus(networkRequest.id, {
                                     instanceUniqueId,
                                     assignmentId: activeAssignment?.id,
                                     providerStatus,
                                     note: providerStatusNote,
                                   })
                                 )
                               }
                             >
                               {networkBusy === "provider-status" ? "Saving..." : "Update Status"}
                             </button>
                             <button
                               className="btn btn-success w-100"
                               disabled={networkBusy === "complete" || !activeAssignment?.id}
                               onClick={() =>
                                 runNetworkAction("complete", async () => {
                                   await completeCoreNetworkAssignment(activeAssignment.id, { instanceUniqueId });
                                   await updateCoreNetworkProviderStatus(networkRequest.id, {
                                     instanceUniqueId,
                                     assignmentId: activeAssignment.id,
                                     providerStatus: "completed",
                                     note: "Marked complete from production module",
                                   });
                                 })
                               }
                             >
                               {networkBusy === "complete" ? "Completing..." : "Mark Complete"}
                             </button>
                           </div>
                         </div>
                       </div>
                     </div>

                     <div className="card border-0 shadow-sm mb-3">
                       <div className="card-body">
                         <div className="d-flex justify-content-between align-items-start flex-wrap gap-3 mb-3">
                           <div>
                             <div className="fw-semibold">Provider Rating</div>
                             <div className="text-muted small">Capture decorator performance after completion.</div>
                           </div>
                           <div className="text-muted small">
                             Existing ratings: {networkRatings.length}
                           </div>
                         </div>
                         <div className="row g-3">
                           {["overallScore", "qualityScore", "communicationScore", "onTimeScore", "defectScore"].map((field) => (
                             <div className="col-md-2" key={field}>
                               <label className="form-label small text-muted">{field.replace(/Score$/, "").replace(/([A-Z])/g, " $1").trim()}</label>
                               <input
                                 className="form-control"
                                 type="number"
                                 min="0"
                                 max="5"
                                 step="0.1"
                                 value={ratingDraft[field]}
                                 onChange={(event) => setRatingDraft((prev) => ({ ...prev, [field]: event.target.value }))}
                               />
                             </div>
                           ))}
                           <div className="col-md-10">
                             <label className="form-label small text-muted">Comments</label>
                             <input
                               className="form-control"
                               value={ratingDraft.comments}
                               onChange={(event) => setRatingDraft((prev) => ({ ...prev, comments: event.target.value }))}
                               placeholder="How did the decorator perform?"
                             />
                           </div>
                           <div className="col-md-2 d-flex align-items-end">
                             <button
                               className="btn btn-outline-primary w-100"
                               disabled={networkBusy === "rating" || !ratedMemberId || !(activeAssignment?.completedAt || String(networkRequest?.status || "").toLowerCase() === "completed")}
                               onClick={() =>
                                 runNetworkAction("rating", () =>
                                   createCoreNetworkRating(networkRequest.id, {
                                     instanceUniqueId,
                                     assignmentId: activeAssignment?.id,
                                     ratedMemberId,
                                     ...ratingDraft,
                                   })
                                 )
                               }
                             >
                               {networkBusy === "rating" ? "Saving..." : "Save Rating"}
                             </button>
                           </div>
                         </div>
                         {!!networkEvents.length && (
                           <div className="mt-3 small text-muted">
                             Latest provider event: {String(networkEvents[networkEvents.length - 1]?.eventType || "").replace(/_/g, " ")}
                           </div>
                         )}
                       </div>
                     </div>
                   </>
                 ) : (
                   <div className="card border-0 shadow-sm">
                     <div className="card-body text-center py-5">
                       <div className="fw-semibold mb-2">No Decorator Network request yet</div>
                       <div className="text-muted mb-3">
                         Create a decoration-production WorkRequest from this production job to start matching decorators.
                       </div>
                       <button
                         className="btn btn-primary"
                         disabled={networkBusy === "create" || !productionJobId}
                         onClick={() =>
                           runNetworkAction("create", async () => {
                             await createCoreNetworkProductionWorkRequest(productionJobId, buildProductionWorkRequestPayload());
                           })
                         }
                       >
                         {networkBusy === "create" ? "Creating..." : "Find Decorator"}
                       </button>
                     </div>
                   </div>
                 )}
               </div>
             )}

             {tab === "processes" && (
               <div className="bg-gray-50 min-h-[500px]">
                 {/* Header */}
                 <div className="bg-gradient-to-b from-white to-gray-50 border-b-2 border-gray-200 px-5 py-4">
                   <div className="flex justify-between items-center">
                     <div>
                       <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 m-0">
                         <svg
                           className="w-5 h-5"
                           fill="none"
                           stroke="currentColor"
                           strokeWidth="2"
                           viewBox="0 0 24 24"
                         >
                           <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                         </svg>
                         Production Processes
                       </h3>
                       <p className="text-sm text-gray-500 mt-1 mb-0">
                         Manufacturing steps for this job
                       </p>
                     </div>
                     <button
                       className="btn btn-primary d-flex align-items-center gap-2"
                       onClick={addProcess}
                     >
                       <svg
                         className="w-4 h-4"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="2.5"
                         viewBox="0 0 24 24"
                       >
                         <path d="M12 5v14M5 12h14" />
                       </svg>
                       Add Process
                     </button>
                   </div>
                 </div>
     
                 {/* Content */}
                 <div className="p-5">
                   {processes?.length > 0 ? (
                     <>
                       {/* Table */}
                       <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                         {/* Header Row */}
                         <div className="grid grid-cols-[60px_2.5fr_2fr_100px_140px_110px_130px_150px] gap-3 px-4 py-3 bg-gray-50 border-b-2 border-gray-200">
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Step
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Name
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Person
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Type
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Machine
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Minutes
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase">
                             Cost ($)
                           </div>
                           {/* <div className="text-xs font-semibold text-gray-600 uppercase">
                             Due Date
                           </div> */}
                           <div className="text-xs font-semibold text-gray-600 uppercase text-center">
                             Actions
                           </div>
                         </div>
     
                         {/* Data Rows */}
                         {processes.map((p, idx) => {
                           const isEditing = editingProcessIdx === idx;
                           const isNew = String(p.id).startsWith("proc-");
     
                           return (
                             <div
                               key={p.id}
                               //   className={`grid grid-cols-[60px_1.5fr_1fr_90px_120px_100px_120px_120px_140px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 ${
                               //     isEditing
                               //       ? "bg-blue-50"
                               //       : "bg-white hover:bg-gray-50"
                               //   }`}
                               // >
                               className={`grid grid-cols-[60px_2.5fr_2fr_100px_140px_110px_130px_150px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 ${
                                 isEditing
                                   ? "bg-blue-50"
                                   : "bg-white hover:bg-gray-50"
                               }`}
                             >
                               {/* Step # */}
                               <input
                                 type="number"
                                 value={p.step_no}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     step_no: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 className={`form-control form-control-sm text-center ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               {/* Name */}
                               <input
                                 type="text"
                                 value={p.name}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     name: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="Process name"
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               {/* Person */}
                               <input
                                 type="text"
                                 value={p.person}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     person: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="Operator"
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               {/* Type */}
                               <select
                                 value={p.type}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     type: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               >
                                 <option value="manual">Manual</option>
                                 <option value="machine">Machine</option>
                               </select>
     
                               {/* Machine */}
                               <input
                                 type="text"
                                 value={p.machine_name || ""}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     machine_name: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="Optional"
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               {/* Est. Minutes */}
                               <input
                                 type="number"
                                 value={p.est_minutes || ""}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     est_minutes: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="0"
                                 className={`form-control form-control-sm text-end ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               {/* Cost */}
                               <input
                                 type="number"
                                 value={p.cost || ""}
                                 onChange={(e) =>
                                   updateAt(setProcesses, idx, {
                                     ...p,
                                     cost: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="0.00"
                                 className={`form-control form-control-sm text-end ${
                                   !isEditing && !isNew ? "bg-gray-50" : ""
                                 }`}
                               />
     
                               <div className="d-flex gap-2 justify-content-center align-items-center">
                                 {isEditing || isNew ? (
                                   <>
                                     {/* Save Button */}
                                     <button
                                       onClick={() => saveProcessRow(idx)}
                                       className="btn btn-sm btn-success d-flex align-items-center gap-1 px-3"
                                       title="Save changes"
                                     >
                                       <i className="bi bi-check-lg"></i>
                                       Save
                                     </button>
     
                                     {/* Cancel Button (only for existing items) */}
                                     {!isNew && (
                                       <button
                                         onClick={cancelEditProcess}
                                         className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                         title="Cancel editing"
                                       >
                                         <i className="bi bi-x-lg"></i>
                                       </button>
                                     )}
     
                                     {/* Delete Button */}
                                     <button
                                       onClick={() => removeProcess(idx)}
                                       className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                       title="Delete process"
                                     >
                                       <i className="bi bi-trash"></i>
                                     </button>
                                   </>
                                 ) : (
                                   <>
                                     {/* Edit Button */}
                                     <button
                                       onClick={() => startEditProcess(idx)}
                                       className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 px-3"
                                       title="Edit process"
                                     >
                                       <i className="bi bi-pencil"></i>
                                       Edit
                                     </button>
     
                                     {/* Delete Button */}
                                     <button
                                       onClick={() => removeProcess(idx)}
                                       className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                       title="Delete process"
                                     >
                                       <i className="bi bi-trash"></i>
                                     </button>
                                   </>
                                 )}
                               </div>
                             </div>
                           );
                         })}
                       </div>
     
                       {/* Info Footer */}
                       <div className="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0">
                         <svg
                           className="w-4 h-4"
                           fill="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                         </svg>
                         <span className="text-sm">
                           Click <strong>Edit</strong> to modify. Changes save
                           individually.
                         </span>
                       </div>
                     </>
                   ) : (
                     /* Empty State */
                     (<div className="text-center py-5 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                       <svg
                         className="w-16 h-16 mx-auto mb-3 text-gray-400"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="1.5"
                         viewBox="0 0 24 24"
                       >
                         <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                         <path d="M9 12h6m-6 4h6" />
                       </svg>
                       <h4 className="font-semibold text-gray-700 mb-2">
                         No Processes Yet
                       </h4>
                       <p className="text-gray-500 mb-3">
                         Click Add Process to define steps
                       </p>
                       <button
                         className="btn btn-outline-primary"
                         onClick={addProcess}
                       >
                         Add First Process
                       </button>
                     </div>)
                   )}
                 </div>
               </div>
             )}
     
             {tab === "bom" && (
               <div className="bg-gray-50 min-h-[500px]">
                 {/* Header Section */}
                 <div className="bg-gradient-to-b from-white to-gray-50 border-b-2 border-gray-200 px-5 py-4">
                   <div className="flex justify-between items-center">
                     <div>
                       <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2 m-0">
                         <svg
                           className="w-5 h-5"
                           fill="none"
                           stroke="currentColor"
                           strokeWidth="2"
                           viewBox="0 0 24 24"
                         >
                           <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                         </svg>
                         Bill of Materials
                       </h3>
                       <p className="text-sm text-gray-500 mt-1 mb-0">
                         Manage components and materials for this job
                       </p>
                     </div>
                     <button
                       className="btn btn-primary d-flex align-items-center gap-2 shadow-sm"
                       onClick={addBOM}
                     >
                       <svg
                         className="w-4 h-4"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="2.5"
                         viewBox="0 0 24 24"
                       >
                         <path d="M12 5v14M5 12h14" />
                       </svg>
                       Add Item
                     </button>
                   </div>
                 </div>
     
                 {/* Table Container */}
                 <div className="p-5">
                   {bom?.length > 0 ? (
                     <>
                       {/* Table Header */}
                       <div className="bg-white border border-gray-200 rounded-t-lg overflow-hidden">
                         <div className="grid grid-cols-[140px_1.8fr_90px_90px_130px_130px_140px] gap-3 px-4 py-3 bg-gray-50 border-b-2 border-gray-200">
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             SKU / Part #
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             Description
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             Quantity
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             Unit
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             Unit Price
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide">
                             Total Cost
                           </div>
                           <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide text-center">
                             Actions
                           </div>
                         </div>
     
                         {/* Table Rows */}
                         {bom.map((b, idx) => {
                           const isEditing = editingBomIdx === idx;
                           const isNew = String(b.id).startsWith("bom-");
                           const totalCost =
                             (parseFloat(b.qtyper) || 0) *
                             (parseFloat(b.unitprice) || 0);
     
                           return (
                             <div
                               key={b.id}
                               className={`grid grid-cols-[140px_1.8fr_90px_90px_130px_130px_140px] gap-3 px-4 py-4 border-b border-gray-100 last:border-b-0 transition-colors ${
                                 isEditing
                                   ? "bg-blue-50"
                                   : "bg-white hover:bg-gray-50"
                               }`}
                             >
                               {/* SKU */}
                               <input
                                 type="text"
                                 value={b.sku}
                                 onChange={(e) =>
                                   updateAt(setBOM, idx, {
                                     ...b,
                                     sku: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="SKU-001"
                                 className={`form-control form-control-sm font-monospace ${
                                   !isEditing && !isNew
                                     ? "bg-gray-50 text-gray-600"
                                     : ""
                                 }`}
                               />
     
                               {/* Description */}
                               <input
                                 type="text"
                                 value={b.description}
                                 onChange={(e) =>
                                   updateAt(setBOM, idx, {
                                     ...b,
                                     description: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="Item description"
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew
                                     ? "bg-gray-50 text-gray-600"
                                     : ""
                                 }`}
                               />
     
                               {/* Quantity */}
                               <input
                                 type="number"
                                 value={b.qtyper}
                                 onChange={(e) =>
                                   updateAt(setBOM, idx, {
                                     ...b,
                                     qtyper: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="0"
                                 className={`form-control form-control-sm text-end ${
                                   !isEditing && !isNew
                                     ? "bg-gray-50 text-gray-600"
                                     : ""
                                 }`}
                               />
     
                               {/* Unit */}
                               <input
                                 type="text"
                                 value={b.unit}
                                 onChange={(e) =>
                                   updateAt(setBOM, idx, {
                                     ...b,
                                     unit: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="pcs"
                                 className={`form-control form-control-sm ${
                                   !isEditing && !isNew
                                     ? "bg-gray-50 text-gray-600"
                                     : ""
                                 }`}
                               />
     
                               {/* Unit Price */}
                               <input
                                 type="number"
                                 value={b.unitprice}
                                 onChange={(e) =>
                                   updateAt(setBOM, idx, {
                                     ...b,
                                     unitprice: e.target.value,
                                   })
                                 }
                                 disabled={!isEditing && !isNew}
                                 placeholder="0.00"
                                 className={`form-control form-control-sm text-end ${
                                   !isEditing && !isNew
                                     ? "bg-gray-50 text-gray-600"
                                     : ""
                                 }`}
                               />
     
                               {/* Total Cost (Calculated) */}
                               <div className="px-3 py-2 bg-green-50 border border-green-200 rounded text-end font-semibold text-green-700 font-monospace text-sm">
                                 {totalCost.toFixed(2)}
                               </div>
     
                               {/* Actions */}
     
                               <div className="d-flex gap-2 justify-content-center align-items-center">
                                 {isEditing || isNew ? (
                                   <>
                                     {/* Save Button */}
                                     <button
                                       onClick={() => saveBomRow(idx)}
                                       className="btn btn-sm btn-success d-flex align-items-center gap-1 px-3"
                                       title="Save changes"
                                     >
                                       <i className="bi bi-check-lg"></i>
                                       Save
                                     </button>
     
                                     {/* Cancel Button (only for existing items) */}
                                     {!isNew && (
                                       <button
                                         onClick={cancelEditBom}
                                         className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1"
                                         title="Cancel editing"
                                       >
                                         <i className="bi bi-x-lg"></i>
                                       </button>
                                     )}
     
                                     {/* Delete Button */}
                                     <button
                                       onClick={() => removeBOM(idx)}
                                       className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                       title="Delete item"
                                     >
                                       <i className="bi bi-trash"></i>
                                     </button>
                                   </>
                                 ) : (
                                   <>
                                     {/* Edit Button */}
                                     <button
                                       onClick={() => startEditBom(idx)}
                                       className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 px-3"
                                       title="Edit item"
                                     >
                                       <i className="bi bi-pencil"></i>
                                       Edit
                                     </button>
     
                                     {/* Delete Button */}
                                     <button
                                       onClick={() => removeBOM(idx)}
                                       className="btn btn-sm btn-outline-danger d-flex align-items-center gap-1"
                                       title="Delete item"
                                     >
                                       <i className="bi bi-trash"></i>
                                     </button>
                                   </>
                                 )}
                               </div>
                             </div>
                           );
                         })}
                       </div>
     
                       {/* Grand Total Section */}
                       <div className="mt-4 p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg d-flex justify-content-between align-items-center">
                         <div>
                           <div className="font-semibold text-green-800 mb-1">
                             Grand Total
                           </div>
                           <div className="text-sm text-green-600">
                             {bom.length} item{bom.length !== 1 ? "s" : ""}
                           </div>
                         </div>
                         <div className="text-3xl font-bold text-green-700 font-monospace">
                           {bom
                             .reduce((sum, b) => {
                               const itemTotal =
                                 (parseFloat(b.qtyper) || 0) *
                                 (parseFloat(b.unitprice) || 0);
                               return sum + itemTotal;
                             }, 0)
                             .toFixed(2)}
                         </div>
                       </div>
     
                       {/* Info Footer */}
                       <div className="alert alert-info d-flex align-items-center gap-2 mt-3 mb-0">
                         <svg
                           className="w-4 h-4 flex-shrink-0"
                           fill="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
                         </svg>
                         <span className="text-sm">
                           <strong>Total Cost</strong> is calculated as{" "}
                           <strong>Quantity × Unit Price</strong>. Click Edit to
                           modify item details.
                         </span>
                       </div>
                     </>
                   ) : (
                     /* Empty State */
                     (<div className="text-center py-5 bg-white border-2 border-dashed border-gray-300 rounded-lg">
                       <svg
                         className="w-16 h-16 mx-auto mb-3 text-gray-400"
                         fill="none"
                         stroke="currentColor"
                         strokeWidth="1.5"
                         viewBox="0 0 24 24"
                       >
                         <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                         <path d="M9 12h6m-6 4h6" />
                       </svg>
                       <h4 className="font-semibold text-gray-700 mb-2">
                         No BOM Items Yet
                       </h4>
                       <p className="text-gray-500 mb-3">
                         Click "Add Item" to start building your bill of materials
                       </p>
                       <button className="btn btn-outline-primary" onClick={addBOM}>
                         Add First Item
                       </button>
                     </div>)
                   )}
                 </div>
               </div>
             )}
     
             {tab === "chat" && (
               <div style={{ padding: 0 }}>
                 <ChatBox
                   orderId={
                     card.orderNumber || detailedCard?.OrderId || "default-order"
                   }
                   assignedUsers={[]}
                   allUsers={[]}
                 />
               </div>
             )}
     
             {/* {tab === "activity" && (
               <div className="p-6 bg-gray-50 min-h-96">
                 <div className="flex items-center justify-between mb-6">
                   <div>
                     <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                       <i className="bi bi-clock-history mr-2 text-blue-600"></i>
                       Activity Logs
                     </h3>
                     <p className="text-xs text-gray-500 mt-1">
                       Order #{card.orderNumber} • {auditEvents.length} events
                     </p>
                   </div>
                 </div>
     
                 <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                   <table className="min-w-full divide-y divide-gray-200 text-sm">
                     <thead className="bg-gray-50">
                       <tr>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           #
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Action
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           From → To
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Machine
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Note
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           User
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Timestamp
                         </th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-100">
                       {auditEvents.length ? (
                         auditEvents.map((ev, index) => (
                           <tr key={ev.id} className="hover:bg-gray-50">
                             <td className="px-4 py-2">{index + 1}</td>
                             <td className="px-4 py-2">
                               <span
                                 className={`px-2 py-1 text-xs rounded ${getEventBadge(
                                   ev.type
                                 )}`}
                               >
                                 {ev.type.replace("_", " ")}
                               </span>
                             </td>
                             <td className="px-4 py-2 text-xs">
                               <span
                                 className={`px-2 py-0.5 rounded ${getStatusBadge(
                                   ev.from
                                 )}`}
                               >
                                 {getStatusLabel(ev.from)}
                               </span>
                               <i className="bi bi-arrow-right text-gray-400 mx-1"></i>
                               <span
                                 className={`px-2 py-0.5 rounded ${getStatusBadge(
                                   ev.to
                                 )}`}
                               >
                                 {getStatusLabel(ev.to)}
                               </span>
                             </td>
                             <td className="px-4 py-2 text-xs">
                               {ev.machineName && (
                                 <>
                                   {ev.machineName}{" "}
                                   {ev.machineCode && `(${ev.machineCode})`}
                                 </>
                               )}
                             </td>
                             <td className="px-4 py-2 text-xs text-gray-700">
                               {ev.note ? parseSimpleNote(ev.note) : "-"}
                             </td>
                             <td className="px-4 py-2 text-xs">
                               {ev.user || "System"}
                             </td>
                             <td className="px-4 py-2 text-xs text-gray-500">
                               {new Date(ev.at).toLocaleString("en-US", {
                                 month: "short",
                                 day: "numeric",
                                 hour: "2-digit",
                                 minute: "2-digit",
                               })}
                             </td>
                           </tr>
                         ))
                       ) : (
                         <tr>
                           <td
                             colSpan={7}
                             className="px-4 py-6 text-center text-gray-500"
                           >
                             No audit logs found
                           </td>
                         </tr>
                       )}
                     </tbody>
                   </table>
                 </div>
               </div>
             )} */}
     
             {tab === "activity" && (
               <div className="p-6 bg-gray-50">
                 <div className="flex items-center justify-between mb-6">
                   <div>
                     <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                       <i className="bi bi-clock-history mr-2 text-blue-600"></i>
                       Activity Logs
                     </h3>
                     <p className="text-xs text-gray-500 mt-1">
                       Order #{card.orderNumber} • {auditEvents.length} events
                     </p>
                   </div>
     
                   {/* ✅ NEW: Filters at top right */}
                   <div className="flex items-center gap-3">
                     {/* Department Filter */}
                     <select
                       className="form-select form-select-sm"
                       value={auditFilters.department}
                       onChange={(e) =>
                         setAuditFilters({
                           ...auditFilters,
                           department: e.target.value,
                         })
                       }
                     >
                       <option value="">All Departments</option>
                       <option value="Embroidery">Embroidery</option>
                       <option value="Screening">Screening</option>
                       <option value="Direct to Film">Direct to Film</option>
                       <option value="Cresting">Cresting</option>
                     </select>
     
                     {/* Event Type Filter */}
                     <select
                       className="form-select form-select-sm"
                       value={auditFilters.types}
                       onChange={(e) =>
                         setAuditFilters({ ...auditFilters, types: e.target.value })
                       }
                     >
                       <option value="">All Events</option>
                       <option value="SCHEDULE">Schedule</option>
                       <option value="START">Start</option>
                       <option value="STATUS_CHANGE">Status Change</option>
                       <option value="PAUSE">Pause</option>
                       <option value="COMPLETE">Complete</option>
                       <option value="MERGE">Merge</option>
                     </select>
     
                     {/* Scope Filter */}
                     <select
                       className="form-select form-select-sm"
                       value={auditFilters.scope}
                       onChange={(e) =>
                         setAuditFilters({ ...auditFilters, scope: e.target.value })
                       }
                     >
                       <option value="full">Full History</option>
                       <option value="card">This Card Only</option>
                     </select>
     
                     <label className="flex items-center gap-2">
                       <input
                         type="checkbox"
                         checked={auditFilters.withMeta}
                         onChange={(e) =>
                           setAuditFilters({
                             ...auditFilters,
                             withMeta: e.target.checked,
                           })
                         }
                         className="form-check-input"
                       />
                       <span className="text-sm">Include Metadata</span>
                     </label>
                   </div>
                 </div>
     
                 <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
                   <table className="min-w-full divide-y divide-gray-200 text-sm">
                     <thead className="bg-gray-50">
                       <tr>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           #
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Action
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           From → To
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Department
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Machine
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Note
                         </th>
                         <th className="px-4 py-2 text-left font-medium text-gray-600">
                           Timestamp
                         </th>
                       </tr>
                     </thead>
                     <tbody className="divide-y divide-gray-100">
                       {auditEvents.length ? (
                         auditEvents.map((ev, index) => (
                           <tr key={index} className="hover:bg-gray-50">
                             <td className="px-4 py-2">{index + 1}</td>
                             <td className="px-4 py-2">
                               <span
                                 className={`px-2 py-1 text-xs rounded ${getEventBadge(
                                   ev.type
                                 )}`}
                               >
                                 {ev.type.replace("_", " ")}
                               </span>
                             </td>
                             <td className="px-4 py-2 text-xs">
                               {ev.from && (
                                 <span
                                   className={`px-2 py-0.5 rounded ${getStatusBadge(
                                     ev.from
                                   )}`}
                                 >
                                   {getStatusLabel(ev.from)}
                                 </span>
                               )}
                               {ev.from && ev.to && (
                                 <i className="bi bi-arrow-right text-gray-400 mx-1"></i>
                               )}
                               {ev.to && (
                                 <span
                                   className={`px-2 py-0.5 rounded ${getStatusBadge(
                                     ev.to
                                   )}`}
                                 >
                                   {getStatusLabel(ev.to)}
                                 </span>
                               )}
                             </td>
                             <td className="px-4 py-2 text-xs">
                               {ev.department || "-"}
                             </td>
                             <td className="px-4 py-2 text-xs">
                               {ev.machineName && (
                                 <>
                                   {ev.machineName}{" "}
                                   {ev.machineCode && `(${ev.machineCode})`}
                                 </>
                               )}
                             </td>
                             <td className="px-4 py-2 text-xs text-gray-700">
                               {ev.note ? parseSimpleNote(ev.note) : "-"}
                             </td>
                             <td className="px-4 py-2 text-xs text-gray-500">
                               {new Date(ev.at).toLocaleString("en-US", {
                                 month: "short",
                                 day: "numeric",
                                 hour: "2-digit",
                                 minute: "2-digit",
                               })}
                             </td>
                           </tr>
                         ))
                       ) : (
                         <tr>
                           <td
                             colSpan={7}
                             className="px-4 py-6 text-center text-gray-500"
                           >
                             No audit logs found
                           </td>
                         </tr>
                       )}
                     </tbody>
                   </table>
                 </div>
               </div>
             )}
     
             {tab === "cost" && (
               <div className="p-6 bg-gradient-to-br from-gray-50 to-gray-100">
                 {/* Header with Summary Stats */}
                 <div className="mb-6">
                   <div className="flex items-center justify-between mb-4">
                     <div>
                       <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                         <i className="bi bi-clock-history text-blue-600"></i>
                         Production Activity & Cost Tracking
                       </h3>
                       <p className="text-sm text-gray-500 mt-1">
                         Order #{card.orderNumber} • {auditEvents.length} total
                         events
                       </p>
                     </div>
                   </div>
     
                   {/* Summary Cards - Time & Cost Analysis */}
                   {auditEvents.length > 0 && (
                     <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                       {/* Total Production Time */}
                       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                               Total Time
                             </p>
                             <p className="text-2xl font-bold text-gray-900">
                               {calculateTotalTime(auditEvents)}
                             </p>
                           </div>
                           <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                             <i className="bi bi-clock text-xl text-blue-600"></i>
                           </div>
                         </div>
                       </div>
     
                       {/* Time in Production */}
                       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                               In Production
                             </p>
                             <p className="text-2xl font-bold text-cyan-600">
                               {calculateStageTime(auditEvents, "In_Production")}
                             </p>
                           </div>
                           <div className="w-12 h-12 bg-cyan-100 rounded-lg flex items-center justify-center">
                             <i className="bi bi-gear-fill text-xl text-cyan-600"></i>
                           </div>
                         </div>
                       </div>
     
                       {/* Time on Hold */}
                       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                               On Hold
                             </p>
                             <p className="text-2xl font-bold text-orange-600">
                               {calculateStageTime(auditEvents, "On_Hold")}
                             </p>
                           </div>
                           <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
                             <i className="bi bi-pause-circle text-xl text-orange-600"></i>
                           </div>
                         </div>
                       </div>
     
                       {/* Estimated Cost */}
                       <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                         <div className="flex items-center justify-between">
                           <div>
                             <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                               Est. Cost
                             </p>
                             <p className="text-2xl font-bold text-green-600">
                               ${calculateProductionCost(auditEvents, card)}
                             </p>
                           </div>
                           <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                             <i className="bi bi-currency-dollar text-xl text-green-600"></i>
                           </div>
                         </div>
                       </div>
                     </div>
                   )}
     
                   {/* Stage Duration Breakdown */}
                   {auditEvents.length > 0 && (
                     <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5 mb-6">
                       <h4 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                         <i className="bi bi-bar-chart text-blue-600"></i>
                         Time Breakdown by Stage
                       </h4>
                       <div className="space-y-3">
                         {getStageBreakdown(auditEvents).map((stage) => (
                           <div key={stage.name}>
                             <div className="flex items-center justify-between mb-1">
                               <span className="text-sm text-gray-700">
                                 {stage.label}
                               </span>
                               <span className="text-sm font-semibold text-gray-900">
                                 {stage.duration}
                               </span>
                             </div>
                             <div className="w-full bg-gray-200 rounded-full h-2">
                               <div
                                 className={`h-2 rounded-full ${stage.color}`}
                                 style={{ width: `${stage.percentage}%` }}
                               ></div>
                             </div>
                           </div>
                         ))}
                       </div>
                     </div>
                   )}
                 </div>
     
                 {/* Timeline View */}
                 <div className="relative">
                   {auditEvents.length ? (
                     <div className="space-y-4">
                       {auditEvents.map((ev, index) => {
                         const noteData = parseDetailedNote(ev.note);
                         const duration = calculateEventDuration(auditEvents, index);
     
                         return (
                           <div key={ev.id} className="relative pl-8">
                             {/* Timeline connector */}
                             {index !== auditEvents.length - 1 && (
                               <div className="absolute left-[11px] top-8 bottom-0 w-0.5 bg-gray-200"></div>
                             )}
                             {/* Timeline dot */}
                             <div
                               className={`absolute left-0 top-1.5 w-6 h-6 rounded-full flex items-center justify-center ${getEventDotColor(
                                 ev.type
                               )}`}
                             >
                               <i
                                 className={`bi ${getEventIcon(
                                   ev.type
                                 )} text-xs text-white`}
                               ></i>
                             </div>
                             {/* Event Card */}
                             <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
                               <div className="flex items-start justify-between gap-4">
                                 {/* Left: Main Content */}
                                 <div className="flex-1">
                                   {/* Action & Status */}
                                   <div className="flex items-center gap-2 mb-3">
                                     <span
                                       className={`px-2.5 py-1 text-xs font-medium rounded-md ${getEventBadge(
                                         ev.type
                                       )}`}
                                     >
                                       {ev.type
                                         ? ev.type.replace(/_/g, " ")
                                         : "Status Change"}
                                     </span>
     
                                     <div className="flex items-center gap-1.5 text-xs">
                                       <span
                                         className={`px-2 py-0.5 rounded ${getStatusBadge(
                                           ev.from
                                         )}`}
                                       >
                                         {getStatusLabel(ev.from)}
                                       </span>
                                       <i className="bi bi-arrow-right text-gray-400"></i>
                                       <span
                                         className={`px-2 py-0.5 rounded ${getStatusBadge(
                                           ev.to
                                         )}`}
                                       >
                                         {getStatusLabel(ev.to)}
                                       </span>
                                     </div>
     
                                     {/* Duration Badge */}
                                     {duration && (
                                       <span className="ml-auto px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded font-medium">
                                         <i className="bi bi-stopwatch mr-1"></i>
                                         {duration}
                                       </span>
                                     )}
                                   </div>
     
                                   {/* Details Grid */}
                                   <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                                     {ev.department && (
                                       <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                         <i className="bi bi-building text-gray-400"></i>
                                         <span>{ev.department}</span>
                                       </div>
                                     )}
     
                                     {(ev.machineName || noteData.machine) && (
                                       <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                         <i className="bi bi-gear text-gray-400"></i>
                                         <span>
                                           {ev.machineName || noteData.machine}
                                           {ev.machineCode && (
                                             <span className="text-gray-400 ml-1">
                                               ({ev.machineCode})
                                             </span>
                                           )}
                                         </span>
                                       </div>
                                     )}
     
                                     {noteData.operator && (
                                       <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                         <i className="bi bi-person text-gray-400"></i>
                                         <span>{noteData.operator}</span>
                                       </div>
                                     )}
     
                                     {noteData.qty && (
                                       <div className="flex items-center gap-1.5 text-xs text-gray-600">
                                         <i className="bi bi-box text-gray-400"></i>
                                         <span>Qty: {noteData.qty}</span>
                                       </div>
                                     )}
                                   </div>
     
                                   {/* Hold Reason */}
                                   {noteData.reason && (
                                     <div className="mt-3 text-xs bg-red-50 text-red-700 px-3 py-2 rounded-md border border-red-100 flex items-start gap-2">
                                       <i className="bi bi-exclamation-triangle-fill mt-0.5"></i>
                                       <div>
                                         <strong>Hold Reason:</strong>{" "}
                                         {noteData.reason}
                                       </div>
                                     </div>
                                   )}
     
                                   {/* Batch Info */}
                                   {noteData.group && (
                                     <div className="mt-2 text-xs bg-blue-50 text-blue-700 px-3 py-2 rounded-md border border-blue-100">
                                       <i className="bi bi-link-45deg mr-1"></i>
                                       Split into batch of {noteData.size} items
                                     </div>
                                   )}
                                 </div>
     
                                 {/* Right: Timestamp */}
                                 <div className="text-right flex-shrink-0">
                                   <div className="text-xs font-medium text-gray-900">
                                     {new Date(ev.at).toLocaleString("en-US", {
                                       month: "short",
                                       day: "numeric",
                                     })}
                                   </div>
                                   <div className="text-xs text-gray-500 mt-0.5">
                                     {new Date(ev.at).toLocaleString("en-US", {
                                       hour: "2-digit",
                                       minute: "2-digit",
                                     })}
                                   </div>
                                 </div>
                               </div>
                             </div>
                           </div>
                         );
                       })}
                     </div>
                   ) : (
                     <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                       <i className="bi bi-inbox text-4xl text-gray-300 mb-3"></i>
                       <p className="text-gray-500">No activity logs found</p>
                     </div>
                   )}
                 </div>
               </div>
             )}
     
             {/* <footer style={{ display: "flex", gap: 8, padding: 12 }}>
               <button style={ui.btnPrimary} onClick={saveMeta}>
                 Save
               </button>
     
               <button style={ui.btnGhost} onClick={onClose}>
                 Close
               </button>
             </footer> */}
             {/* Only show footer for overview, processes, and bom tabs */}
             {
               // tab !== "details" &&
               tab !== "overview" &&
                 tab !== "overview2" &&
                 tab !== "proofing" &&
                 tab !== "chat" &&
                 tab !== "activity" &&
                 tab !== "processes" &&
                 tab !== "bom" &&
                 tab !== "cost" && (
                   <footer className="flex gap-3 px-5 py-4 border-t border-gray-200 bg-gray-50 justify-end items-center sticky bottom-0 z-10">
                     <button
                       className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex items-center gap-2"
                       onClick={onClose}
                     >
                       <i className="bi bi-x-lg text-xs"></i>
                       Close
                     </button>
     
                     <button
                       className="px-5 py-2 text-sm font-semibold text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 shadow-sm"
                       onClick={saveMeta}
                     >
                       <i className="bi bi-check-lg"></i>
                       Save Changes
                     </button>
                   </footer>
                 )
             }
           </div>
         </div>
    </div>
  );
}

export default JobCardModal;
